import { ChipImportJobs } from '../components/chip-import-jobs';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { faSquare as faSquareRegular } from '@fortawesome/free-regular-svg-icons';
import {
  faCheck,
  faCheckCircle,
  faDownload,
  faExternalLinkAlt,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SmartChipRunUploadMetadata, JobAcceptedResponse, zJobAcceptedResponse } from '@resistapp/common/api-types';
import { decorateSampleLayoutsWithWater } from '@resistapp/common/smartchip-water-decoration';
import { LabWorkbookStatus } from '@resistapp/common/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Link as RouterLink } from 'react-router-dom';

import type { DispenserPrefix } from '@resistapp/common/types';
import { API_URL, getAuthHeaders, resistomapApiClient } from '../../../utils/fetch';
import type { SmartChipRunLogEntry, SmartChipRunSummary, SmartChipSampleLayout } from '../api/use-lab-workbook';
import { LabPageHeader } from '../components/lab-page-header';
import { SmartChipGridView } from '../components/smartchip-grid';
import { SmartChipPipettingVolumes, type PipettingSystem } from '../components/smartchip-pipetting-volumes';
import { useLabLayoutContext } from '../lab-layout';
import {
  canIncludeSmartChipRunLogInPayload,
  canUploadSmartChipRunFilesForTab,
  canUseSmartChipRunTabDetails,
  getLowestMissingCompleteSmartChipIndex,
  getSmartChipNameFromSaveError,
  getSyncedSelectedLayoutIndex,
  isSmartChipNameSaveErrorForRow,
  normaliseSmartChipRunLogChipIndex,
  resolveSmartChipRunLogChipIndex,
} from './smartchip-run-page-state-utils';

type SmartChip = SmartChipRunSummary['chips'][number];

type RunLogRemoteFile = {
  filename: string;
  url: string;
};

type RunLogExistingFiles = {
  cts: RunLogRemoteFile | null;
  raw: RunLogRemoteFile | null;
};

type RunLogFormRow = {
  id: string;
  persistedId: string | null;
  chipId: number | null;
  chipName: string | null;
  chipIndex: number | null;
  canonicalKey: string | null;
  runDate: string;
  comment: string;
  invoice: string;
  dispenser: number | null;
  dispenserPrefix: DispenserPrefix | null;
  cycler: number | null;
  kitLot: string;
  mmLot: string;
  waterLot: string;
  glycerolLot: string;
  antifoamBLot: string;
  preparerInitials: string;
  uploaderInitials: string;
  ctsFile: File | null;
  rawFile: File | null;
  fileError: string | null;
  existingFiles: RunLogExistingFiles | null;
};

function resolveChipName(row: RunLogFormRow, chips: SmartChip[]): string | null {
  if (row.chipName && row.chipName.trim().length > 0) {
    return row.chipName.trim();
  }
  if (row.chipId != null) {
    const chip = chips.find(candidate => candidate.id === row.chipId);
    if (chip) {
      const trimmed = chip.name.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return null;
}

function buildChipFilename(chipName: string, fallback: string): string {
  const trimmed = chipName.trim();
  const safe = trimmed
    .replace(/[^\w\-.]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 120);
  const fallbackSafe = fallback.trim().replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const base = safe.length > 0 ? safe : fallbackSafe.length > 0 ? fallbackSafe : 'smartchip-run';
  return `${base}.md`;
}

type SmartChipRunUpdatePayload = {
  chipsToRun: number;
  selectedLayoutIndex: number;
  runLogs: Array<{
    id: string | null;
    chipId: number | null;
    chipIndex: number | null;
    chipName: string | null;
    runDateIso: string | null;
    comment: string | null;
    invoice: string | null;
    dispenser: number | null;
    dispenserPrefix: DispenserPrefix | null;
    cycler: number | null;
    kitLot: string | null;
    mmLot: string | null;
    waterLot: string | null;
    glycerolLot: string | null;
    antifoamBLot: string | null;
    preparerInitials: string;
    uploaderInitials: string | null;
  }>;
};

type UpdatePayload = {
  defaultMmLot?: string | null;
  status?: LabWorkbookStatus;
  smartChipRun?: SmartChipRunUpdatePayload;
};

type MutationInput = {
  workbookId: string;
  payload: UpdatePayload;
  successMessage: string;
  uploads?: RunLogUploadInput[];
};

type DispenserOption = { label: string; value: number; prefix: DispenserPrefix; valueKey: string };
const DISPENSER_OPTIONS: readonly DispenserOption[] = [
  { label: 'MSND1', value: 1, prefix: 'MSND', valueKey: 'MSND:1' },
  { label: 'MSND2', value: 2, prefix: 'MSND', valueKey: 'MSND:2' },
  { label: 'MSND3', value: 3, prefix: 'MSND', valueKey: 'MSND:3' },
  { label: 'MSND5', value: 5, prefix: 'MSND', valueKey: 'MSND:5' },
  { label: 'SCND1', value: 1, prefix: 'SCND', valueKey: 'SCND:1' },
] as const;

const CYCLER_OPTIONS = [
  { label: 'C72', value: 72 },
  { label: 'C83', value: 83 },
  { label: 'C84', value: 84 },
  { label: 'C94', value: 94 },
  { label: 'C100', value: 100 },
  { label: 'C101', value: 101 },
] as const;

function resolvePipettingSystemFromDispenserPrefix(prefix: DispenserPrefix | null): PipettingSystem | null {
  if (prefix === 'MSND' || prefix === 'SCND') {
    return prefix;
  }
  return null;
}

const INITIALS_PATTERN = /^[A-Z]{3}$/;
const DUPLICATE_CHIP_NAME_MESSAGE = 'Chip name already used on another chip tab. Choose a unique name.';

function normalizeInitialsInput(value: string): string {
  return value
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3);
}

function isValidInitials(value: string): boolean {
  return INITIALS_PATTERN.test(value);
}

type RunLogFilesUpdate = Partial<Pick<RunLogFormRow, 'ctsFile' | 'rawFile' | 'fileError'>>;

type RunLogUploadInput = {
  rowId: string;
  metadata: SmartChipRunUploadMetadata;
  ctsFile?: File | null;
  rawFile?: File | null;
};

type WorkbookPatchCompletedError = Error & {
  workbookPatchCompleted?: true;
};

function markWorkbookPatchCompleted(error: Error): WorkbookPatchCompletedError {
  return Object.assign(error, { workbookPatchCompleted: true as const });
}

function workbookPatchCompletedBeforeError(error: unknown): boolean {
  return error instanceof Error && (error as WorkbookPatchCompletedError).workbookPatchCompleted === true;
}

type RunLogDraft = {
  chipName: string | null;
  runDate: string;
  comment: string;
  invoice: string;
  dispenser: number | null;
  dispenserPrefix: DispenserPrefix | null;
  cycler: number | null;
  kitLot: string;
  mmLot: string;
  waterLot: string;
  glycerolLot: string;
  antifoamBLot: string;
  preparerInitials: string;
  uploaderInitials: string;
};

type SharedLotFieldKey = 'mmLot' | 'waterLot' | 'glycerolLot' | 'antifoamBLot';
type KitLotFieldKey = 'kitLot';

type LotFieldKey = SharedLotFieldKey | KitLotFieldKey;

type LotFieldConfig<Key extends LotFieldKey> = {
  key: Key;
  label: string;
  placeholder: string;
};

const SHARED_LOT_FIELDS: ReadonlyArray<LotFieldConfig<SharedLotFieldKey>> = [
  {
    key: 'mmLot',
    label: 'SmartChip TB Green Gene Expression Master Mix Lot',
    placeholder: 'Enter mastermix lot identifier',
  },
  { key: 'waterLot', label: 'qPCR Water Lot', placeholder: 'Enter qPCR water lot identifier' },
  { key: 'glycerolLot', label: 'Glycerol Lot', placeholder: 'Enter glycerol lot identifier' },
  { key: 'antifoamBLot', label: 'Antifoam B Lot', placeholder: 'Enter Antifoam B lot identifier' },
] as const;

const PER_CHIP_LOT_FIELDS: ReadonlyArray<LotFieldConfig<KitLotFieldKey>> = [
  { key: 'kitLot', label: 'SmartChip MyDesign Kit Lot', placeholder: 'Enter kit lot identifier' },
] as const;

const RUN_DRAFT_STORAGE_PREFIX = 'smartchip-run-drafts:';

type ChipTabDescriptor = {
  key: string;
  defaultLabel: string;
  chipDisplayName: string | null;
  chipId: number | null;
  chipIndex: number | null;
  chipNumber: number | null;
  layout: SmartChipSampleLayout | null;
  isAdHoc: boolean;
  isPlaceholder: boolean;
  isEnabled: boolean;
  associatedRowId?: string;
};

function generateRowId(): string {
  try {
    return globalThis.crypto.randomUUID();
  } catch {
    return `run-log-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function resolveRunLogKey(row: RunLogFormRow): string {
  if (row.chipId != null) {
    return `chip:${row.chipId}`;
  }
  if (row.chipIndex != null) {
    return `layout:${row.chipIndex}`;
  }
  if (row.persistedId) {
    return `persisted:${row.persistedId}`;
  }
  if (row.canonicalKey) {
    return row.canonicalKey;
  }
  return `local:${row.id}`;
}

function loadRunDrafts(storageKey: string | null): Map<string, RunLogDraft> {
  if (!storageKey || typeof window === 'undefined') {
    return new Map();
  }
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return new Map();
    }
    const parsed = JSON.parse(rawValue) as Record<string, RunLogDraft | undefined>;
    return new Map(
      Object.entries(parsed)
        .filter(([, value]) => value != null)
        .map(([key, value]) => [key, value as RunLogDraft]),
    );
  } catch {
    return new Map();
  }
}

function persistRunDrafts(storageKey: string | null, drafts: Map<string, RunLogDraft>): void {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }
  if (drafts.size === 0) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  const serializable: Record<string, RunLogDraft> = {};
  drafts.forEach((value, key) => {
    serializable[key] = value;
  });
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(serializable));
  } catch {
    // Ignore storage failures (e.g., quota exceeded)
  }
}

function shouldPersistDraft(row: RunLogFormRow): boolean {
  return !row.persistedId && row.chipId == null;
}

function extractDraftFromRow(row: RunLogFormRow): RunLogDraft {
  return {
    chipName: row.chipName ?? null,
    runDate: row.runDate,
    comment: row.comment,
    invoice: row.invoice,
    dispenser: row.dispenser,
    dispenserPrefix: row.dispenserPrefix,
    cycler: row.cycler,
    kitLot: row.kitLot,
    mmLot: row.mmLot,
    waterLot: row.waterLot,
    glycerolLot: row.glycerolLot,
    antifoamBLot: row.antifoamBLot,
    preparerInitials: row.preparerInitials,
    uploaderInitials: row.uploaderInitials,
  };
}

function applyDraftToRow(row: RunLogFormRow, draft: RunLogDraft): RunLogFormRow {
  return {
    ...row,
    chipName: draft.chipName ?? row.chipName,
    runDate: draft.runDate,
    comment: draft.comment,
    invoice: draft.invoice,
    dispenser: draft.dispenser ?? row.dispenser,
    dispenserPrefix: draft.dispenserPrefix ?? row.dispenserPrefix,
    cycler: draft.cycler ?? row.cycler,
    kitLot: draft.kitLot,
    mmLot: draft.mmLot,
    waterLot: draft.waterLot,
    glycerolLot: draft.glycerolLot,
    antifoamBLot: draft.antifoamBLot,
    preparerInitials: draft.preparerInitials,
    uploaderInitials: draft.uploaderInitials,
  };
}

function applyDraftsToRows(rows: RunLogFormRow[], drafts: Map<string, RunLogDraft>): RunLogFormRow[] {
  const next = rows.map(row => {
    if (!shouldPersistDraft(row)) {
      return row;
    }
    const draft = drafts.get(resolveRunLogKey(row));
    if (!draft) {
      return row;
    }
    return applyDraftToRow(row, draft);
  });
  const hasChanges = next.some((row, index) => row !== rows[index]);
  return hasChanges ? next : rows;
}

function rowRequiresPreparerInitials(row: RunLogFormRow): boolean {
  if (row.persistedId) {
    return true;
  }
  if (row.ctsFile || row.rawFile) {
    return true;
  }
  if (row.preparerInitials.length > 0) {
    return true;
  }
  if (
    (row.chipName ?? '').trim().length > 0 ||
    row.runDate.trim().length > 0 ||
    row.comment.trim().length > 0 ||
    row.invoice.trim().length > 0 ||
    row.kitLot.trim().length > 0 ||
    row.dispenser != null ||
    row.cycler != null
  ) {
    return true;
  }
  return false;
}

function rowRequiresUploaderInitials(row: RunLogFormRow): boolean {
  if (row.chipId != null) {
    return true;
  }
  if (row.ctsFile || row.rawFile) {
    return true;
  }
  if (row.existingFiles?.cts || row.existingFiles?.raw) {
    return true;
  }
  if (row.uploaderInitials.length > 0) {
    return true;
  }
  return false;
}

function rowPreparerInitialsAreValid(row: RunLogFormRow): boolean {
  if (!rowRequiresPreparerInitials(row)) {
    return true;
  }
  return isValidInitials(row.preparerInitials);
}

function rowUploaderInitialsAreValid(row: RunLogFormRow): boolean {
  if (!rowRequiresUploaderInitials(row)) {
    return true;
  }
  return isValidInitials(row.uploaderInitials);
}

function rowHasPersistableData(row: RunLogFormRow): boolean {
  const hasPersistedChip = row.chipId != null || row.persistedId != null;
  if (hasPersistedChip) {
    return true;
  }
  if ((row.chipName ?? '').trim().length > 0) {
    return true;
  }
  if (row.runDate.trim().length > 0) {
    return true;
  }
  if (row.comment.trim().length > 0 || row.invoice.trim().length > 0 || row.kitLot.trim().length > 0) {
    return true;
  }
  if (
    row.mmLot.trim().length > 0 ||
    row.waterLot.trim().length > 0 ||
    row.glycerolLot.trim().length > 0 ||
    row.antifoamBLot.trim().length > 0
  ) {
    return (row.chipName ?? '').trim().length > 0;
  }
  if (row.preparerInitials.length > 0 || row.uploaderInitials.length > 0) {
    return true;
  }
  if (row.dispenser != null || row.cycler != null) {
    return true;
  }
  if (row.ctsFile || row.rawFile) {
    return true;
  }
  return false;
}

function shouldIncludeRowInPayload(row: RunLogFormRow): boolean {
  return rowHasPersistableData(row);
}

type CanonicalRunLogRow = {
  key: string;
  persistedId: string | null;
  chipId: number | null;
  chipName: string | null;
  chipIndex: number | null;
  runDate: string;
  comment: string;
  invoice: string;
  dispenser: number | null;
  dispenserPrefix: DispenserPrefix | null;
  cycler: number | null;
  kitLot: string;
  mmLot: string;
  waterLot: string;
  glycerolLot: string;
  antifoamBLot: string;
  preparerInitials: string;
  uploaderInitials: string;
  existingFiles: RunLogExistingFiles | null;
};

function trimmedOrNull(value: string | null | undefined): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeText(value: string | null | undefined): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function formatIsoDate(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length < 10) {
    return '';
  }
  return iso.slice(0, 10);
}

function createCanonicalRunLogRow(chip: SmartChip, chipIndex: number | null): CanonicalRunLogRow {
  return {
    key: `chip:${chip.id}`,
    persistedId: null,
    chipId: chip.id,
    chipName: trimmedOrNull(chip.name),
    chipIndex,
    runDate: formatIsoDate(chip.runDateIso),
    comment: chip.comment ?? '',
    invoice: chip.invoice ?? '',
    dispenser: chip.dispenser ?? null,
    dispenserPrefix: chip.dispenserPrefix ?? null,
    cycler: chip.cycler ?? null,
    kitLot: normalizeText(chip.kitLot),
    mmLot: normalizeText(chip.mmLot ?? null),
    waterLot: normalizeText(chip.waterLot ?? null),
    glycerolLot: normalizeText(chip.glycerolLot ?? null),
    antifoamBLot: normalizeText(chip.antifoamBLot ?? null),
    preparerInitials: normalizeInitialsInput(chip.preparerInitials ?? ''),
    uploaderInitials: normalizeInitialsInput(chip.uploaderInitials ?? ''),
    existingFiles: {
      cts: chip.files.cts ? { filename: chip.files.cts.filename, url: chip.files.cts.url } : null,
      raw: chip.files.raw ? { filename: chip.files.raw.filename, url: chip.files.raw.url } : null,
    },
  };
}

function buildCanonicalRunLogMap(
  chips: SmartChip[],
  runLogs: SmartChipRunLogEntry[],
  chipIndexById: Map<number, number>,
  tabTargetCount: number,
): Map<string, CanonicalRunLogRow> {
  const canonical = new Map<string, CanonicalRunLogRow>();
  const chipById = new Map<number, SmartChip>(chips.map(chip => [chip.id, chip]));
  const chipByNormalizedName = new Map<string, { chip: SmartChip; chipIndex: number }>();
  const persistedChipIndexByChipId = new Map<number, number>();
  const usedChipIndices = new Set<number>();

  runLogs.forEach(entry => {
    if (entry.chipId == null || persistedChipIndexByChipId.has(entry.chipId)) {
      return;
    }
    const chipIndex = normaliseSmartChipRunLogChipIndex(entry.chipIndex, tabTargetCount);
    if (chipIndex != null) {
      persistedChipIndexByChipId.set(entry.chipId, chipIndex);
    }
  });

  const claimChipIndex = (): number | null => {
    for (let index = 0; index < tabTargetCount; index += 1) {
      if (!usedChipIndices.has(index)) {
        usedChipIndices.add(index);
        return index;
      }
    }
    return null;
  };

  chips.forEach((chip, fallbackIndex) => {
    const chipIndex = persistedChipIndexByChipId.get(chip.id) ?? chipIndexById.get(chip.id) ?? fallbackIndex;
    usedChipIndices.add(chipIndex);
    const normalizedName = trimmedOrNull(chip.name);
    if (normalizedName) {
      const nameKey = normalizedName.toLowerCase();
      if (!chipByNormalizedName.has(nameKey)) {
        chipByNormalizedName.set(nameKey, { chip, chipIndex });
      }
    }
    const baseRow = createCanonicalRunLogRow(chip, chipIndex);
    canonical.set(baseRow.key, baseRow);
  });

  runLogs.forEach((entry, entryIndex) => {
    let chipId = entry.chipId ?? null;
    let chip = chipId != null ? chipById.get(chipId) ?? null : null;
    const chipIndexFromChipId = chipId != null ? chipIndexById.get(chipId) ?? null : null;
    const entryChipIndex = normaliseSmartChipRunLogChipIndex(entry.chipIndex, tabTargetCount);
    const fallbackChipIndex =
      chipId != null
        ? resolveSmartChipRunLogChipIndex({
            persistedChipIndex: entry.chipIndex,
            chipIndexFromChipId,
            tabTargetCount,
          })
        : entryChipIndex ?? claimChipIndex() ?? null;
    let resolvedChipIndex = fallbackChipIndex;
    if (resolvedChipIndex != null) {
      usedChipIndices.add(resolvedChipIndex);
    }
    const chipNameFromLog = trimmedOrNull(entry.chipName);
    if (!chip && chipNameFromLog) {
      const nameKey = chipNameFromLog.toLowerCase();
      const chipByName = chipByNormalizedName.get(nameKey);
      if (chipByName) {
        chip = chipByName.chip;
        chipId = chipByName.chip.id;
        resolvedChipIndex = entryChipIndex ?? chipByName.chipIndex;
        usedChipIndices.add(resolvedChipIndex);
      }
    }
    const key =
      chipId != null
        ? `chip:${chipId}`
        : resolvedChipIndex != null
          ? `layout:${resolvedChipIndex}`
          : entry.id
            ? `persisted:${entry.id}`
            : chipNameFromLog
              ? `chip-name:${chipNameFromLog.toLowerCase()}`
              : `run:${entryIndex}`;
    const existing = canonical.get(key);
    const baseFiles =
      existing?.existingFiles ??
      (chip
        ? {
            cts: chip.files.cts ? { filename: chip.files.cts.filename, url: chip.files.cts.url } : null,
            raw: chip.files.raw ? { filename: chip.files.raw.filename, url: chip.files.raw.url } : null,
          }
        : null);
    const previous = existing ?? {
      key,
      persistedId: null,
      chipId,
      chipName: chip ? trimmedOrNull(chip.name) : chipNameFromLog,
      chipIndex: resolvedChipIndex,
      runDate: chip ? formatIsoDate(chip.runDateIso) : '',
      comment: chip?.comment ?? '',
      invoice: chip?.invoice ?? '',
      dispenser: chip?.dispenser ?? null,
      dispenserPrefix: chip?.dispenserPrefix ?? null,
      cycler: chip?.cycler ?? null,
      kitLot: normalizeText(chip?.kitLot ?? null),
      mmLot: normalizeText(chip?.mmLot ?? null),
      waterLot: normalizeText(chip?.waterLot ?? null),
      glycerolLot: normalizeText(chip?.glycerolLot ?? null),
      antifoamBLot: normalizeText(chip?.antifoamBLot ?? null),
      preparerInitials: normalizeInitialsInput(chip?.preparerInitials ?? ''),
      uploaderInitials: normalizeInitialsInput(chip?.uploaderInitials ?? ''),
      existingFiles: baseFiles,
    };

    const runDate = entry.runDateIso != null ? formatIsoDate(entry.runDateIso) : previous.runDate;
    const comment = entry.comment ?? previous.comment;
    const invoice = entry.invoice ?? previous.invoice;
    const dispenser =
      entry.dispenser != null ? entry.dispenser : previous.dispenser ?? (chip ? chip.dispenser ?? null : null);
    const dispenserPrefix =
      entry.dispenserPrefix != null
        ? (entry.dispenserPrefix as DispenserPrefix | null)
        : previous.dispenserPrefix ?? chip?.dispenserPrefix ?? null;
    const cycler = entry.cycler != null ? entry.cycler : previous.cycler ?? (chip ? chip.cycler ?? null : null);
    const kitLotCandidate =
      typeof entry.kitLot === 'string' && entry.kitLot.trim().length > 0 ? entry.kitLot.trim() : previous.kitLot;
    const mmLotCandidate =
      typeof entry.mmLot === 'string' && entry.mmLot.trim().length > 0 ? entry.mmLot.trim() : previous.mmLot;
    const waterLotCandidate =
      typeof entry.waterLot === 'string' && entry.waterLot.trim().length > 0
        ? entry.waterLot.trim()
        : previous.waterLot;
    const glycerolLotCandidate =
      typeof entry.glycerolLot === 'string' && entry.glycerolLot.trim().length > 0
        ? entry.glycerolLot.trim()
        : previous.glycerolLot;
    const antifoamBLotCandidate =
      typeof entry.antifoamBLot === 'string' && entry.antifoamBLot.trim().length > 0
        ? entry.antifoamBLot.trim()
        : previous.antifoamBLot;
    const preparer =
      entry.preparerInitials != null && entry.preparerInitials.trim().length > 0
        ? normalizeInitialsInput(entry.preparerInitials)
        : previous.preparerInitials;
    const uploader =
      entry.uploaderInitials != null && entry.uploaderInitials.trim().length > 0
        ? normalizeInitialsInput(entry.uploaderInitials)
        : previous.uploaderInitials;

    const updated: CanonicalRunLogRow = {
      key,
      persistedId: entry.id ?? previous.persistedId,
      chipId: chipId ?? previous.chipId ?? null,
      chipName: chipNameFromLog ?? previous.chipName ?? null,
      chipIndex: entryChipIndex ?? resolvedChipIndex ?? previous.chipIndex ?? null,
      runDate,
      comment,
      invoice,
      dispenser,
      dispenserPrefix,
      cycler,
      kitLot: kitLotCandidate,
      mmLot: mmLotCandidate,
      waterLot: waterLotCandidate,
      glycerolLot: glycerolLotCandidate,
      antifoamBLot: antifoamBLotCandidate,
      preparerInitials: preparer,
      uploaderInitials: uploader,
      existingFiles: baseFiles,
    };

    canonical.set(key, updated);
  });

  return canonical;
}

function instantiateRunLogRow(source: CanonicalRunLogRow, existing?: RunLogFormRow): RunLogFormRow {
  // Canonical workbook refreshes can race with user edits (e.g. after saving start details).
  // Preserve locally-entered initials if the server/canonical value is still empty.
  const preparerInitials =
    source.preparerInitials.length > 0
      ? source.preparerInitials
      : existing?.preparerInitials ?? source.preparerInitials;
  const uploaderInitials =
    source.uploaderInitials.length > 0
      ? source.uploaderInitials
      : existing?.uploaderInitials ?? source.uploaderInitials;

  return {
    id: existing?.id ?? generateRowId(),
    persistedId: source.persistedId,
    chipId: source.chipId,
    chipName: source.chipName,
    chipIndex: source.chipIndex,
    canonicalKey: source.key,
    runDate: source.runDate,
    comment: source.comment,
    invoice: source.invoice,
    dispenser: source.dispenser,
    dispenserPrefix: source.dispenserPrefix,
    cycler: source.cycler,
    kitLot: source.kitLot,
    mmLot: source.mmLot,
    waterLot: source.waterLot,
    glycerolLot: source.glycerolLot,
    antifoamBLot: source.antifoamBLot,
    preparerInitials,
    uploaderInitials,
    ctsFile: existing?.ctsFile ?? null,
    rawFile: existing?.rawFile ?? null,
    fileError: existing?.fileError ?? null,
    existingFiles: source.existingFiles ?? existing?.existingFiles ?? null,
  };
}

function areRunLogRowsSynced(a: RunLogFormRow, b: RunLogFormRow): boolean {
  return (
    a.persistedId === b.persistedId &&
    a.chipId === b.chipId &&
    a.chipName === b.chipName &&
    a.chipIndex === b.chipIndex &&
    a.runDate === b.runDate &&
    a.comment === b.comment &&
    a.invoice === b.invoice &&
    a.dispenser === b.dispenser &&
    a.dispenserPrefix === b.dispenserPrefix &&
    a.cycler === b.cycler &&
    a.kitLot === b.kitLot &&
    a.mmLot === b.mmLot &&
    a.waterLot === b.waterLot &&
    a.glycerolLot === b.glycerolLot &&
    a.antifoamBLot === b.antifoamBLot &&
    a.preparerInitials === b.preparerInitials &&
    a.uploaderInitials === b.uploaderInitials &&
    a.ctsFile === b.ctsFile &&
    a.rawFile === b.rawFile &&
    a.fileError === b.fileError &&
    ((a.existingFiles === null && b.existingFiles === null) ||
      (a.existingFiles !== null &&
        b.existingFiles !== null &&
        a.existingFiles.cts?.url === b.existingFiles.cts?.url &&
        a.existingFiles.cts?.filename === b.existingFiles.cts?.filename &&
        a.existingFiles.raw?.url === b.existingFiles.raw?.url &&
        a.existingFiles.raw?.filename === b.existingFiles.raw?.filename))
  );
}

function rowHasCanonicalChanges(row: RunLogFormRow, canonicalRunLogMap: Map<string, CanonicalRunLogRow>): boolean {
  const canonical = canonicalRunLogMap.get(resolveRunLogKey(row));
  if (!canonical) {
    return true;
  }
  const canonicalRow = instantiateRunLogRow(canonical);
  return !areRunLogRowsSynced(row, canonicalRow);
}

function mergeRunLogState(
  previous: RunLogFormRow[],
  canonical: Map<string, CanonicalRunLogRow>,
  drafts: Map<string, RunLogDraft>,
): {
  rows: RunLogFormRow[];
  drafts: Map<string, RunLogDraft>;
  draftsChanged: boolean;
  mutated: boolean;
} {
  if (canonical.size === 0) {
    return { rows: previous, drafts, draftsChanged: false, mutated: false };
  }

  const previousByKey = new Map<string, RunLogFormRow>();
  previous.forEach(row => {
    previousByKey.set(resolveRunLogKey(row), row);
  });

  const persistedById = new Map<string, RunLogFormRow>();
  previous.forEach(row => {
    if (row.persistedId) {
      persistedById.set(row.persistedId, row);
    }
  });

  const layoutByIndex = new Map<number, RunLogFormRow>();
  previous.forEach(row => {
    if (row.chipId == null && row.chipIndex != null) {
      layoutByIndex.set(row.chipIndex, row);
    }
  });

  const usedKeys = new Set<string>();
  const nextRows: RunLogFormRow[] = [];
  let draftsChanged = false;
  let nextDrafts: Map<string, RunLogDraft> = drafts;

  canonical.forEach((entry, key) => {
    let existing = previousByKey.get(key) ?? null;
    let existingKey = existing ? resolveRunLogKey(existing) : null;

    if (!existing && entry.persistedId && persistedById.has(entry.persistedId)) {
      existing = persistedById.get(entry.persistedId) ?? null;
      existingKey = existing ? resolveRunLogKey(existing) : null;
    }

    if (!existing && entry.chipIndex != null) {
      const layoutRow = layoutByIndex.get(entry.chipIndex);
      if (layoutRow) {
        existing = layoutRow;
        existingKey = resolveRunLogKey(layoutRow);
      }
    }

    if (existingKey) {
      usedKeys.add(existingKey);
      previousByKey.delete(existingKey);
    }

    const merged = instantiateRunLogRow(entry, existing ?? undefined);
    const resolvedKey = resolveRunLogKey(merged);
    usedKeys.add(resolvedKey);

    if (!existing) {
      if (shouldPersistDraft(merged)) {
        const draft = drafts.get(resolvedKey);
        if (draft) {
          const drafted = applyDraftToRow(merged, draft);
          merged.chipName = drafted.chipName;
          merged.runDate = drafted.runDate;
          merged.comment = drafted.comment;
          merged.invoice = drafted.invoice;
          merged.dispenser = drafted.dispenser;
          merged.cycler = drafted.cycler;
          merged.kitLot = drafted.kitLot;
          merged.mmLot = drafted.mmLot;
          merged.waterLot = drafted.waterLot;
          merged.glycerolLot = drafted.glycerolLot;
          merged.antifoamBLot = drafted.antifoamBLot;
          merged.preparerInitials = drafted.preparerInitials;
          merged.uploaderInitials = drafted.uploaderInitials;
        }
      }
      nextRows.push(merged);
      return;
    }

    if (existingKey && existingKey !== resolvedKey && drafts.has(existingKey) && shouldPersistDraft(existing)) {
      if (!draftsChanged) {
        nextDrafts = new Map(drafts);
      }
      nextDrafts.delete(existingKey);
      draftsChanged = true;
    }

    const canonicalKeyChanged = existing.canonicalKey !== merged.canonicalKey;
    const rowsAreSynced = !canonicalKeyChanged && areRunLogRowsSynced(existing, merged);
    nextRows.push(rowsAreSynced ? existing : merged);
  });

  previous.forEach(row => {
    const key = resolveRunLogKey(row);
    if (!usedKeys.has(key)) {
      nextRows.push(row);
    }
  });

  let rowsAreIdentical = nextRows.length === previous.length;
  if (rowsAreIdentical) {
    for (let index = 0; index < nextRows.length; index += 1) {
      if (nextRows[index] !== previous[index]) {
        rowsAreIdentical = false;
        break;
      }
    }
  }
  const resolvedDrafts = nextDrafts;
  if (rowsAreIdentical) {
    return { rows: previous, drafts: resolvedDrafts, draftsChanged, mutated: false };
  }

  return { rows: nextRows, drafts: resolvedDrafts, draftsChanged, mutated: true };
}

function buildIsoDate(value: string): string | null {
  if (!value) {
    return null;
  }
  const iso = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(iso.getTime())) {
    return null;
  }
  return iso.toISOString();
}

type StatusUpdaterCarrier = {
  statusUpdatedByName?: unknown;
  statusUpdatedBy?: unknown;
};

function hasStatusUpdaterCarrier(value: unknown): value is StatusUpdaterCarrier {
  return typeof value === 'object' && value !== null && ('statusUpdatedByName' in value || 'statusUpdatedBy' in value);
}

function extractStatusUpdaterName(value: unknown): string | null {
  if (!hasStatusUpdaterCarrier(value)) {
    return null;
  }
  if (typeof value.statusUpdatedByName === 'string') {
    const trimmed = value.statusUpdatedByName.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  if (typeof value.statusUpdatedBy === 'string') {
    const trimmed = value.statusUpdatedBy.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return null;
}

export function SmartChipRunPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { workbookQuery, viewerAccess, readOnly } = useLabLayoutContext();
  const { data, error, isLoading, isError } = workbookQuery;

  const mutation = useMutation({
    mutationFn: async ({ workbookId, payload, uploads }: MutationInput) => {
      if (!workbookId) {
        throw new Error('Missing workbook identifier');
      }
      await resistomapApiClient.patch(`/lab/${workbookId}`, payload, {
        headers: getAuthHeaders('application/json'),
      });
      const uploadResults: JobAcceptedResponse[] = [];
      if (uploads?.length) {
        for (const upload of uploads) {
          const formData = new FormData();
          formData.append('metadata', JSON.stringify(upload.metadata));
          if (upload.ctsFile) {
            formData.append('ctsFile', upload.ctsFile);
          }
          if (upload.rawFile) {
            formData.append('rawFile', upload.rawFile);
          }
          try {
            const response = await resistomapApiClient.post<JobAcceptedResponse>(
              `/lab/${workbookId}/runs/upload`,
              formData,
              { headers: getAuthHeaders() },
            );
            uploadResults.push(zJobAcceptedResponse.parse(response.data));
          } catch (uploadError) {
            const message = uploadError instanceof Error ? uploadError.message : 'Unknown error';
            throw markWorkbookPatchCompleted(
              new Error(`Failed to upload files for ${upload.metadata.chipName}: ${message}`),
            );
          }
        }
      }
      return uploadResults;
    },
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['labWorkbook', variables.workbookId] });
      await queryClient.invalidateQueries({ queryKey: ['workbookJobs', variables.workbookId] });
      toast({
        status: 'success',
        title: 'Workbook updated',
        description: variables.successMessage,
        duration: 4000,
        isClosable: true,
      });
    },
    onError: async (mutationError, variables) => {
      if (workbookPatchCompletedBeforeError(mutationError)) {
        await queryClient.invalidateQueries({ queryKey: ['labWorkbook', variables.workbookId] });
        await queryClient.invalidateQueries({ queryKey: ['workbookJobs', variables.workbookId] });
      }
      const message = mutationError instanceof Error ? mutationError.message : 'Failed to update workbook';
      toast({
        status: 'error',
        title: 'Update failed',
        description: message,
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const workbook = data?.workbook;
  const smartChip = data?.smartChip;
  const sampleSheet = data?.sampleSheet;
  const configurationId = smartChip?.configurationId ?? data?.primerPrep?.configurationId ?? null;
  const rawSampleLayouts = useMemo<SmartChipSampleLayout[]>(
    () => smartChip?.sampleLayouts ?? [],
    [smartChip?.sampleLayouts],
  );
  // Apply H2O placeholder wells decoration for specific configurations (248:6, 296:5, 80:21).
  // This ensures the UI displays H2O in the required wells. The same decoration is applied
  // when generating mydesign files - see src/server/modules/lab/handlers/handle-get-smartchip-mydesign.ts
  // around line 36. Configuration is defined in src/common/smartchip-water-decoration.ts.
  const sampleLayouts = useMemo<SmartChipSampleLayout[]>(
    () => decorateSampleLayoutsWithWater<SmartChipSampleLayout>(rawSampleLayouts, configurationId),
    [rawSampleLayouts, configurationId],
  );
  const sampleLayoutByChipIndex = useMemo(() => {
    const map = new Map<number, SmartChipSampleLayout>();
    sampleLayouts.forEach(layout => {
      if (layout.chipIndex != null) {
        map.set(layout.chipIndex, layout);
      }
    });
    return map;
  }, [sampleLayouts]);
  const chips = useMemo<SmartChip[]>(() => smartChip?.chips ?? [], [smartChip?.chips]);
  const suggestedChipCount = smartChip?.suggestedChipCount ?? null;
  const configuredChipCount = workbook?.numChips ?? 0;
  const tabTargetCount = Math.max(configuredChipCount, chips.length);
  const draftStorageKey = useMemo(
    () => (workbook?.id ? `${RUN_DRAFT_STORAGE_PREFIX}${workbook.id}` : null),
    [workbook?.id],
  );
  const draftsRef = useRef<Map<string, RunLogDraft>>(new Map());
  const chipTabButtonByKeyRef = useRef<Map<string, HTMLButtonElement | null>>(new Map());
  const defaultChipCount = useMemo(() => {
    if (suggestedChipCount != null && suggestedChipCount > 0) {
      return suggestedChipCount;
    }
    const candidateCounts = [sampleLayouts.length, chips.length, configuredChipCount].filter(count => count > 0);
    if (candidateCounts.length === 0) {
      return 1;
    }
    return Math.max(...candidateCounts);
  }, [chips.length, configuredChipCount, sampleLayouts.length, suggestedChipCount]);
  const completeLayoutIndices = useMemo(() => {
    const indices = new Set<number>();
    sampleLayouts.forEach(layout => {
      if (!layout.isComplete) {
        return;
      }
      if (layout.chipIndex == null) {
        return;
      }
      if (layout.chipIndex >= tabTargetCount) {
        return;
      }
      indices.add(layout.chipIndex);
    });
    return indices;
  }, [sampleLayouts, tabTargetCount]);
  const runDetailsReadOnly = readOnly.smartChipRun;
  const workbookDefaultMmLot = workbook?.defaultMmLot?.trim() ?? '';
  const projectLeadDisplay = workbook?.projectLead?.trim() ?? '';
  const hasProjectLead = projectLeadDisplay.length > 0;
  const labPartnerNoteRaw = workbook?.notes ?? null;
  const labPartnerNoteTrimmed = labPartnerNoteRaw?.trim() ?? '';
  const hasLabPartnerNote = labPartnerNoteTrimmed.length > 0;
  const statusUpdatedByDisplay = extractStatusUpdaterName(workbook);
  const linkedProjects = data?.linkedProjects ?? [];
  const showStatusUpdatedBy =
    statusUpdatedByDisplay != null &&
    statusUpdatedByDisplay.length > 0 &&
    (!hasProjectLead ||
      statusUpdatedByDisplay.localeCompare(projectLeadDisplay, undefined, { sensitivity: 'accent' }) !== 0);

  const chipIndexById = useMemo(() => {
    const map = new Map<number, number>();
    chips.forEach((chip, index) => {
      map.set(chip.id, index);
    });
    return map;
  }, [chips]);

  const chipKitLotById = useMemo(() => {
    const map = new Map<number, string | null>();
    chips.forEach(chip => {
      map.set(chip.id, chip.kitLot ?? null);
    });
    return map;
  }, [chips]);

  const chipDetailsById = useMemo(() => {
    const map = new Map<number, SmartChip>();
    chips.forEach(chip => {
      map.set(chip.id, chip);
    });
    return map;
  }, [chips]);

  const [chipsToRun, setChipsToRun] = useState(() => {
    const persisted = smartChip?.chipsToRun;
    return persisted != null && persisted > 0 ? persisted : defaultChipCount;
  });
  const [selectedLayoutIndex, setSelectedLayoutIndex] = useState(() => {
    const persisted = smartChip?.selectedLayoutIndex;
    return persisted != null && persisted >= 0 ? persisted : 0;
  });
  const lastWorkbookIdRef = useRef<string | null>(workbook?.id ?? null);
  const lastPersistedSelectedLayoutIndexRef = useRef<number | null>(smartChip?.selectedLayoutIndex ?? null);
  const [runLogs, setRunLogs] = useState<RunLogFormRow[]>([]);
  const [pendingSavedChiplessRunLogIds, setPendingSavedChiplessRunLogIds] = useState<Set<string>>(() => new Set());
  const [downloadingRows, setDownloadingRows] = useState<Set<string>>(() => new Set());
  const [pipettingSystemByRunLogId, setPipettingSystemByRunLogId] = useState<Partial<Record<string, PipettingSystem>>>(
    {},
  );
  const runLogEntries = smartChip?.runLogs;
  const canonicalRunLogMap = useMemo(
    () => buildCanonicalRunLogMap(chips, runLogEntries ?? [], chipIndexById, tabTargetCount),
    [chipIndexById, chips, runLogEntries, tabTargetCount],
  );

  useEffect(() => {
    setRunLogs(previous => {
      const {
        rows,
        drafts: mergedDrafts,
        draftsChanged,
      } = mergeRunLogState(previous, canonicalRunLogMap, draftsRef.current);
      if (draftsChanged) {
        draftsRef.current = mergedDrafts;
        if (draftStorageKey) {
          persistRunDrafts(draftStorageKey, mergedDrafts);
        }
      }
      return rows === previous ? previous : rows;
    });
  }, [canonicalRunLogMap, draftStorageKey]);

  const patchRunLog = useCallback(
    (rowId: string, update: Partial<RunLogFormRow>) => {
      if (runDetailsReadOnly) {
        return;
      }
      setRunLogs(previous => {
        const targetIndex = previous.findIndex(row => row.id === rowId);
        if (targetIndex === -1) {
          return previous;
        }
        const currentRow = previous[targetIndex];
        const tentativeRow: RunLogFormRow = { ...currentRow, ...update };
        const next = previous.slice();
        const mutatedRows: Array<{ previous: RunLogFormRow; next: RunLogFormRow }> = [];

        const rowsAreSynced = areRunLogRowsSynced(currentRow, tentativeRow);
        const resolvedRow = rowsAreSynced ? currentRow : tentativeRow;
        next[targetIndex] = resolvedRow;
        if (!rowsAreSynced) {
          mutatedRows.push({ previous: currentRow, next: resolvedRow });
        }

        if (mutatedRows.length === 0) {
          return previous;
        }

        if (draftStorageKey) {
          mutatedRows.forEach(({ previous: previousRow, next: nextRow }) => {
            const previousKey = resolveRunLogKey(previousRow);
            const nextKey = resolveRunLogKey(nextRow);
            if (previousKey !== nextKey) {
              draftsRef.current.delete(previousKey);
            }
            if (shouldPersistDraft(nextRow)) {
              draftsRef.current.set(nextKey, extractDraftFromRow(nextRow));
            } else {
              draftsRef.current.delete(nextKey);
            }
          });
          persistRunDrafts(draftStorageKey, draftsRef.current);
        }

        return next;
      });
    },
    [draftStorageKey, runDetailsReadOnly],
  );

  const handleRunLogFilesChange = useCallback(
    (rowId: string, update: RunLogFilesUpdate) => {
      if (runDetailsReadOnly) {
        return;
      }
      patchRunLog(rowId, update);
    },
    [patchRunLog, runDetailsReadOnly],
  );

  const setDownloadingForRow = useCallback((rowId: string, isDownloading: boolean) => {
    setDownloadingRows(previous => {
      const already = previous.has(rowId);
      if (isDownloading === already) {
        return previous;
      }
      const next = new Set(previous);
      if (isDownloading) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  }, []);

  const handleDownloadMyDesign = useCallback(
    async (row: RunLogFormRow, params: { chipIndex: number | null; fallbackLabel: string }) => {
      if (!workbook) {
        return;
      }
      const chipName = resolveChipName(row, chips);
      if (!chipName) {
        toast({
          status: 'error',
          title: 'Add a chip name',
          description: 'Enter a chip name before downloading the SmartChip layout file.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (params.chipIndex == null) {
        toast({
          status: 'error',
          title: 'Layout unavailable',
          description: 'Sample layout data is not available for this chip yet.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setDownloadingForRow(row.id, true);
      try {
        const query = new URLSearchParams({
          chipIndex: String(params.chipIndex),
          chipName,
        });
        const response = await fetch(`${API_URL}/lab/${workbook.id}/runs/mydesign?${query.toString()}`, {
          method: 'GET',
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          const raw = await response.text();
          let message = raw;
          try {
            const parsed = JSON.parse(raw) as { message?: string };
            if (parsed.message) {
              message = parsed.message;
            }
          } catch {
            // Ignore JSON parse errors and fall back to raw string
          }
          throw new Error(message || 'Failed to download SmartChip layout.');
        }

        const blob = await response.blob();
        let filename = buildChipFilename(chipName, params.fallbackLabel);
        const disposition = response.headers.get('content-disposition');
        if (disposition) {
          const match = disposition.match(/filename="?([^";]+)"?/i);
          if (match?.[1]) {
            filename = match[1];
          }
        }

        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 0);
      } catch (downloadError) {
        const message = downloadError instanceof Error ? downloadError.message : 'Failed to download SmartChip layout.';
        toast({
          status: 'error',
          title: 'Download failed',
          description: message,
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setDownloadingForRow(row.id, false);
      }
    },
    [chips, setDownloadingForRow, toast, workbook],
  );

  useEffect(() => {
    const persisted = smartChip?.chipsToRun;
    setChipsToRun(persisted != null && persisted > 0 ? persisted : defaultChipCount);
  }, [defaultChipCount, smartChip?.chipsToRun]);

  useEffect(() => {
    if (!draftStorageKey) {
      draftsRef.current = new Map();
      return;
    }
    const loadedDrafts = loadRunDrafts(draftStorageKey);
    draftsRef.current = loadedDrafts;
    setRunLogs(previous => applyDraftsToRows(previous, loadedDrafts));
  }, [draftStorageKey]);

  useEffect(() => {
    if (!draftStorageKey) {
      return;
    }
    const keysToDelete: string[] = [];
    draftsRef.current.forEach((_draft, draftKey) => {
      const matchingRow = runLogs.find(row => resolveRunLogKey(row) === draftKey);
      if (!matchingRow || !shouldPersistDraft(matchingRow)) {
        keysToDelete.push(draftKey);
      }
    });
    if (keysToDelete.length > 0) {
      keysToDelete.forEach(key => draftsRef.current.delete(key));
      persistRunDrafts(draftStorageKey, draftsRef.current);
    }
  }, [runLogs, draftStorageKey]);

  useEffect(() => {
    setDownloadingRows(previous => {
      const validIds = new Set(runLogs.map(row => row.id));
      const next = new Set<string>();
      previous.forEach(rowId => {
        if (validIds.has(rowId)) {
          next.add(rowId);
        }
      });
      if (next.size === previous.size) {
        return previous;
      }
      return next;
    });
  }, [runLogs]);

  useEffect(() => {
    setPendingSavedChiplessRunLogIds(previous => {
      if (previous.size === 0) {
        return previous;
      }
      const rowById = new Map(runLogs.map(row => [row.id, row] as const));
      const next = new Set<string>();
      previous.forEach(rowId => {
        const row = rowById.get(rowId);
        if (row && row.chipId == null && row.persistedId == null) {
          next.add(rowId);
        }
      });
      if (next.size === previous.size) {
        return previous;
      }
      return next;
    });
  }, [runLogs]);

  useEffect(() => {
    setPipettingSystemByRunLogId(previous => {
      const rowById = new Map(runLogs.map(row => [row.id, row] as const));
      const nextEntries = Object.entries(previous).filter(([rowId]) => {
        const row = rowById.get(rowId);
        if (!row) {
          return false;
        }
        return resolvePipettingSystemFromDispenserPrefix(row.dispenserPrefix) == null;
      });
      if (nextEntries.length === Object.keys(previous).length) {
        return previous;
      }
      return Object.fromEntries(nextEntries) as Partial<Record<string, PipettingSystem>>;
    });
  }, [runLogs]);

  const baseTabs = useMemo<ChipTabDescriptor[]>(() => {
    if (tabTargetCount <= 0) {
      return [];
    }
    const tabs: ChipTabDescriptor[] = [];
    const occupiedChipIndices = new Set<number>();

    chips.forEach((chip, index) => {
      const key = `chip:${chip.id}`;
      const mappedChipIndex = chipIndexById.get(chip.id);
      const canonicalChipIndex = canonicalRunLogMap.get(key)?.chipIndex ?? null;
      const resolvedChipIndex = canonicalChipIndex ?? mappedChipIndex ?? index;
      const chipNumber = resolvedChipIndex + 1;
      occupiedChipIndices.add(resolvedChipIndex);
      const layout = sampleLayoutByChipIndex.get(resolvedChipIndex) ?? null;
      const chipNameTrimmed = chip.name ? chip.name.trim() : '';
      const chipLayoutLabel = layout?.chipLabel ? layout.chipLabel.trim() : '';
      const chipDisplayName =
        chipNameTrimmed.length > 0 ? chipNameTrimmed : chipLayoutLabel.length > 0 ? chipLayoutLabel : null;
      tabs.push({
        key,
        defaultLabel: `Chip ${chipNumber}`,
        chipDisplayName,
        chipId: chip.id,
        chipIndex: resolvedChipIndex,
        chipNumber,
        layout,
        isAdHoc: false,
        isPlaceholder: false,
        isEnabled: true,
      });
    });

    const nextRunnableChipIndex = getLowestMissingCompleteSmartChipIndex({
      tabTargetCount,
      occupiedChipIndices,
      completeLayoutIndices,
    });

    for (let chipNumber = 1; chipNumber <= tabTargetCount; chipNumber += 1) {
      const chipIndex = chipNumber - 1;
      if (occupiedChipIndices.has(chipIndex)) {
        continue;
      }
      const key = `layout:${chipIndex}`;
      tabs.push({
        key,
        defaultLabel: `Chip ${chipNumber}`,
        chipDisplayName: null,
        chipId: null,
        chipIndex,
        chipNumber,
        layout: sampleLayoutByChipIndex.get(chipIndex) ?? null,
        isAdHoc: false,
        isPlaceholder: true,
        isEnabled: nextRunnableChipIndex === chipIndex,
      });
    }

    return tabs;
  }, [canonicalRunLogMap, chipIndexById, chips, completeLayoutIndices, sampleLayoutByChipIndex, tabTargetCount]);

  const extraTabs = useMemo<ChipTabDescriptor[]>(() => {
    if (runLogs.length === 0) {
      return [];
    }
    const baseKeys = new Set(baseTabs.map(tab => tab.key));
    const extras: ChipTabDescriptor[] = [];
    runLogs.forEach((row, index) => {
      const key = resolveRunLogKey(row);
      if (baseKeys.has(key)) {
        return;
      }
      const label =
        row.chipName && row.chipName.trim().length > 0
          ? row.chipName
          : row.chipId != null
            ? `Chip ${row.chipId}`
            : `Run ${index + 1}`;
      extras.push({
        key,
        defaultLabel: label,
        chipDisplayName: row.chipName ?? null,
        chipId: row.chipId,
        chipIndex: row.chipIndex,
        layout: null,
        isAdHoc: true,
        chipNumber: null,
        isPlaceholder: false,
        isEnabled: true,
        associatedRowId: row.id,
      });
    });
    return extras;
  }, [baseTabs, runLogs]);

  const chipTabs = useMemo<ChipTabDescriptor[]>(() => {
    if (!baseTabs.length && !extraTabs.length) {
      return [];
    }
    const unique = new Map<string, ChipTabDescriptor>();
    baseTabs.forEach(tab => {
      if (!unique.has(tab.key)) {
        unique.set(tab.key, tab);
      }
    });
    extraTabs.forEach(tab => {
      if (!unique.has(tab.key)) {
        unique.set(tab.key, tab);
      }
    });
    return Array.from(unique.values());
  }, [baseTabs, extraTabs]);

  useEffect(() => {
    if (!baseTabs.length) {
      return;
    }
    setRunLogs(previous => {
      const next = previous.slice();
      const existingByKey = new Map<string, RunLogFormRow>();
      next.forEach(row => {
        existingByKey.set(resolveRunLogKey(row), row);
      });
      let mutated = false;

      for (const tab of baseTabs) {
        const existing = existingByKey.get(tab.key);
        if (!existing) {
          const canonical = canonicalRunLogMap.get(tab.key);
          let newRow: RunLogFormRow;
          if (canonical) {
            newRow = instantiateRunLogRow(canonical);
          } else {
            const chipDetails = tab.chipId != null ? chipDetailsById.get(tab.chipId) ?? null : null;
            const chipName = tab.chipDisplayName ?? (chipDetails?.name ? chipDetails.name.trim() : null);
            const fallbackKitLot = tab.chipId != null ? chipKitLotById.get(tab.chipId) ?? '' : '';
            newRow = {
              id: generateRowId(),
              persistedId: null,
              chipId: tab.chipId,
              chipName,
              chipIndex: tab.chipIndex,
              canonicalKey: tab.key,
              runDate: chipDetails?.runDateIso ? chipDetails.runDateIso.slice(0, 10) : '',
              comment: chipDetails?.comment ?? '',
              invoice: chipDetails?.invoice ?? '',
              dispenser: chipDetails?.dispenser ?? null,
              dispenserPrefix: chipDetails?.dispenserPrefix ?? null,
              cycler: chipDetails?.cycler ?? null,
              kitLot: chipDetails?.kitLot ?? fallbackKitLot,
              mmLot: chipDetails?.mmLot ?? workbookDefaultMmLot,
              waterLot: chipDetails?.waterLot ?? '',
              glycerolLot: chipDetails?.glycerolLot ?? '',
              antifoamBLot: chipDetails?.antifoamBLot ?? '',
              preparerInitials: normalizeInitialsInput(chipDetails?.preparerInitials ?? ''),
              uploaderInitials: normalizeInitialsInput(chipDetails?.uploaderInitials ?? ''),
              ctsFile: null,
              rawFile: null,
              fileError: null,
              existingFiles: null,
            };
          }

          if (shouldPersistDraft(newRow)) {
            const draft = draftsRef.current.get(tab.key);
            if (draft) {
              newRow = applyDraftToRow(newRow, draft);
            }
          }
          next.push(newRow);
          existingByKey.set(resolveRunLogKey(newRow), newRow);
          mutated = true;
        }
      }

      if (!mutated) {
        return previous;
      }
      return next;
    });
  }, [baseTabs, canonicalRunLogMap, chipKitLotById, chipDetailsById, workbookDefaultMmLot]);

  const tabCount = chipTabs.length;
  const activeTabIndex = tabCount > 0 ? Math.min(selectedLayoutIndex, tabCount - 1) : 0;
  const activeTabKey = chipTabs[activeTabIndex]?.key ?? null;

  useEffect(() => {
    const currentWorkbookId = workbook?.id ?? null;
    const didWorkbookChange = lastWorkbookIdRef.current !== currentWorkbookId;
    const previousPersistedSelectedLayoutIndex = lastPersistedSelectedLayoutIndexRef.current;
    const persistedSelectedLayoutIndex = smartChip?.selectedLayoutIndex;

    setSelectedLayoutIndex(current =>
      getSyncedSelectedLayoutIndex({
        currentSelectedLayoutIndex: current,
        persistedSelectedLayoutIndex,
        previousPersistedSelectedLayoutIndex: didWorkbookChange ? null : previousPersistedSelectedLayoutIndex,
        didWorkbookChange,
        tabCount,
      }),
    );

    lastWorkbookIdRef.current = currentWorkbookId;
    lastPersistedSelectedLayoutIndexRef.current =
      typeof persistedSelectedLayoutIndex === 'number' && persistedSelectedLayoutIndex >= 0
        ? persistedSelectedLayoutIndex
        : null;
  }, [smartChip?.selectedLayoutIndex, tabCount, workbook?.id]);

  useEffect(() => {
    if (!activeTabKey) {
      return;
    }
    const button = chipTabButtonByKeyRef.current.get(activeTabKey) ?? null;
    button?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTabKey]);

  const runLogsByKey = useMemo(() => {
    const map = new Map<string, RunLogFormRow>();
    runLogs.forEach(row => {
      map.set(resolveRunLogKey(row), row);
    });
    return map;
  }, [runLogs]);

  const persistableRunLogKeys = useMemo(() => {
    const keys = new Set<string>();
    chipTabs.forEach(tab => {
      if (
        canUseSmartChipRunTabDetails({
          canViewRun: viewerAccess.canViewRun,
          isPlaceholder: tab.isPlaceholder,
          isEnabled: tab.isEnabled,
          layoutIsComplete: tab.layout?.isComplete ?? false,
        })
      ) {
        keys.add(tab.key);
      }
    });
    return keys;
  }, [chipTabs, viewerAccess.canViewRun]);

  const resolveRunLogForTab = useCallback(
    (tab: ChipTabDescriptor) =>
      runLogsByKey.get(tab.key) ??
      (tab.associatedRowId ? runLogs.find(row => row.id === tab.associatedRowId) ?? null : null),
    [runLogs, runLogsByKey],
  );

  const resolvePipettingSystem = useCallback(
    (row: RunLogFormRow): PipettingSystem => {
      const fromDispenser = resolvePipettingSystemFromDispenserPrefix(row.dispenserPrefix);
      if (fromDispenser) {
        return fromDispenser;
      }
      const selected = pipettingSystemByRunLogId[row.id];
      if (selected) {
        return selected;
      }
      return 'MSND';
    },
    [pipettingSystemByRunLogId],
  );

  const handlePipettingSystemChange = useCallback((rowId: string, nextSystem: PipettingSystem) => {
    setPipettingSystemByRunLogId(previous => {
      if (previous[rowId] === nextSystem) {
        return previous;
      }
      return { ...previous, [rowId]: nextSystem };
    });
  }, []);

  const duplicateChipNameConflictByRowId = useMemo(() => {
    const conflicts = new Map<string, string>();
    const normalizedByName = new Map<string, string>();
    runLogs.forEach(row => {
      if (row.chipId != null) {
        // Persisted chips keep their assigned name; avoid blocking edits elsewhere.
        return;
      }
      const trimmed = row.chipName ? row.chipName.trim() : '';
      if (!trimmed) {
        return;
      }
      const normalized = trimmed.toLocaleLowerCase('en-US');
      const existingRowId = normalizedByName.get(normalized);
      if (existingRowId && existingRowId !== row.id) {
        conflicts.set(row.id, trimmed);
        conflicts.set(existingRowId, trimmed);
        return;
      }
      normalizedByName.set(normalized, row.id);
    });
    return conflicts;
  }, [runLogs]);

  const buildSmartChipRunUpdatePayload = useCallback(
    (defaultMmLot: string | null, savedRunLogRowId: string): UpdatePayload => {
      const payload: UpdatePayload = {
        defaultMmLot,
      };
      if (smartChip) {
        const rowsForPayload = runLogs
          .filter(row => {
            const isPersistedRow = row.persistedId != null || row.chipId != null;
            const hasPayloadData = shouldIncludeRowInPayload(row);
            if (!hasPayloadData) {
              return false;
            }
            const runLogKey = resolveRunLogKey(row);
            if (
              !canIncludeSmartChipRunLogInPayload({
                isPersistedRow,
                isCurrentSavedRow: row.id === savedRunLogRowId,
                isPendingSavedRow: pendingSavedChiplessRunLogIds.has(row.id),
                tabCanPersistRunDetails: persistableRunLogKeys.has(runLogKey),
                rowHasPersistableData: hasPayloadData,
              })
            ) {
              return false;
            }
            const normalizedPreparer = normalizeInitialsInput(row.preparerInitials);
            const preparerIsValid = isValidInitials(normalizedPreparer);
            if (isPersistedRow && !preparerIsValid) {
              const hasChanges = rowHasCanonicalChanges(row, canonicalRunLogMap);
              if (!hasChanges) {
                // Skip legacy rows with invalid initials when the user hasn't edited them.
                return false;
              }
            }
            return true;
          })
          .map(current => {
            const chipName = resolveChipName(current, chips);
            const normalizedPreparer = normalizeInitialsInput(current.preparerInitials);
            const normalizedUploader = normalizeInitialsInput(current.uploaderInitials);
            return {
              id: current.persistedId,
              chipId: current.chipId,
              chipIndex: current.chipIndex,
              chipName,
              runDateIso: buildIsoDate(current.runDate),
              comment: current.comment.trim() ? current.comment.trim() : null,
              invoice: current.invoice.trim() ? current.invoice.trim() : null,
              dispenser: current.dispenser,
              dispenserPrefix: current.dispenserPrefix,
              cycler: current.cycler,
              kitLot: current.kitLot.trim() ? current.kitLot.trim() : null,
              mmLot: current.mmLot.trim() ? current.mmLot.trim() : null,
              waterLot: current.waterLot.trim() ? current.waterLot.trim() : null,
              glycerolLot: current.glycerolLot.trim() ? current.glycerolLot.trim() : null,
              antifoamBLot: current.antifoamBLot.trim() ? current.antifoamBLot.trim() : null,
              preparerInitials: normalizedPreparer,
              uploaderInitials: normalizedUploader ? normalizedUploader : null,
            };
          });
        payload.smartChipRun = {
          chipsToRun,
          selectedLayoutIndex,
          runLogs: rowsForPayload,
        };
      }
      return payload;
    },
    [
      canonicalRunLogMap,
      chips,
      chipsToRun,
      pendingSavedChiplessRunLogIds,
      persistableRunLogKeys,
      runLogs,
      selectedLayoutIndex,
      smartChip,
    ],
  );

  const handleSaveRunDetails = useCallback(
    async (row: RunLogFormRow) => {
      if (runDetailsReadOnly || !workbook) {
        return;
      }

      const needsPreparerInitials = rowRequiresPreparerInitials(row);
      if (needsPreparerInitials && !isValidInitials(row.preparerInitials)) {
        toast({
          status: 'error',
          title: 'Add preparer initials',
          description: 'Enter three-letter initials for the technician who prepared this chip before saving.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const needsUploaderInitials = rowRequiresUploaderInitials(row);
      if (needsUploaderInitials && !isValidInitials(row.uploaderInitials)) {
        toast({
          status: 'error',
          title: 'Add uploader initials',
          description: 'Enter three-letter initials for the person uploading the run files before saving.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const normalizedMmLot = row.mmLot.trim() ? row.mmLot.trim() : null;
      const normalizedWaterLot = row.waterLot.trim() ? row.waterLot.trim() : null;
      const normalizedGlycerolLot = row.glycerolLot.trim() ? row.glycerolLot.trim() : null;
      const normalizedAntifoamBLot = row.antifoamBLot.trim() ? row.antifoamBLot.trim() : null;
      const trimmedKitLot = row.kitLot.trim();
      const normalizedKitLot = trimmedKitLot ? trimmedKitLot : null;
      const resolvedChipName = resolveChipName(row, chips);
      const hasExistingChip = row.chipId != null;
      const hasSelectedCts = row.ctsFile != null;
      const hasSelectedRaw = row.rawFile != null;
      const hasExistingCts = Boolean(row.existingFiles?.cts);
      const hasExistingRaw = Boolean(row.existingFiles?.raw);
      // Chips uploaded before RAW-only rollout can use their already stored RAW.
      const generateFromStoredRaw = hasExistingRaw && !hasExistingCts && !hasSelectedRaw && !hasSelectedCts;
      const canOverwriteFiles = viewerAccess.isResistomapAdmin && hasExistingCts && hasExistingRaw;
      const overwriteRequested = canOverwriteFiles && (hasSelectedCts || hasSelectedRaw);
      const normalizedPreparerInitials = normalizeInitialsInput(row.preparerInitials);
      const normalizedUploaderInitials = normalizeInitialsInput(row.uploaderInitials);

      if (overwriteRequested && !hasSelectedRaw) {
        patchRunLog(row.id, { fileError: 'Raw is required to overwrite existing results.' });
        toast({
          status: 'error',
          title: 'Overwrite requires Raw',
          description: 'Select Raw to overwrite existing SmartChip results.',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (!overwriteRequested && hasSelectedCts && hasExistingCts) {
        patchRunLog(row.id, { fileError: 'CTS file is already stored for this chip.' });
        toast({
          status: 'error',
          title: 'CTS already uploaded',
          description: 'This chip already has CTS data stored. Remove the file to avoid a re-upload.',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (!overwriteRequested && hasSelectedRaw && hasExistingRaw) {
        patchRunLog(row.id, { fileError: 'Raw file is already stored for this chip.' });
        toast({
          status: 'error',
          title: 'Raw already uploaded',
          description: 'This chip already has a Raw file stored. Remove the file to avoid a re-upload.',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (hasSelectedCts && !hasSelectedRaw) {
        patchRunLog(row.id, { fileError: 'Select CTS and Raw together to verify a CTS export.' });
        toast({
          status: 'error',
          title: 'Raw file required',
          description: 'Select CTS and Raw together to verify a CTS export.',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      if (!resolvedChipName) {
        patchRunLog(row.id, { fileError: 'Enter a chip name before uploading files.' });
        toast({
          status: 'error',
          title: 'Missing chip name',
          description: 'Add a chip name before uploading results for this chip.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (duplicateChipNameConflictByRowId.has(row.id)) {
        toast({
          status: 'error',
          title: 'Duplicate chip name',
          description: DUPLICATE_CHIP_NAME_MESSAGE,
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const requiresKitLot = hasExistingChip || hasSelectedCts || hasSelectedRaw || hasExistingCts || hasExistingRaw;
      if (requiresKitLot && !normalizedKitLot) {
        toast({
          status: 'error',
          title: 'Add kit Lot',
          description: 'Enter the SmartChip MyDesign Kit Lot before recording run details for this chip.',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!hasExistingChip) {
        if (!normalizedMmLot) {
          toast({
            status: 'error',
            title: 'Add mastermix Lot',
            description: 'Enter the SmartChip TB Green Gene Expression Master Mix Lot before creating a new chip.',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        if (!normalizedWaterLot) {
          toast({
            status: 'error',
            title: 'Add qPCR Water Lot',
            description: 'Enter the qPCR Water Lot before creating a new chip.',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        if (!normalizedGlycerolLot) {
          toast({
            status: 'error',
            title: 'Add Glycerol Lot',
            description: 'Enter the Glycerol Lot before creating a new chip.',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        if (!normalizedAntifoamBLot) {
          toast({
            status: 'error',
            title: 'Add Antifoam B Lot',
            description: 'Enter the Antifoam B Lot before creating a new chip.',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        if (!row.runDate.trim()) {
          toast({
            status: 'error',
            title: 'Add run date',
            description: 'Enter the run date before creating a new chip.',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        if (row.dispenser == null || row.cycler == null) {
          toast({
            status: 'error',
            title: 'Select dispenser and cycler',
            description: 'Choose the dispenser and cycler before creating a new chip.',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      const uploads: RunLogUploadInput[] = [];
      if (hasSelectedCts || hasSelectedRaw || generateFromStoredRaw) {
        const metadata: SmartChipRunUploadMetadata = {
          chipId: row.chipId,
          chipName: resolvedChipName,
          runDateIso: buildIsoDate(row.runDate),
          comment: row.comment.trim() ? row.comment.trim() : null,
          invoice: row.invoice.trim() ? row.invoice.trim() : null,
          dispenser: row.dispenser,
          dispenserPrefix: row.dispenserPrefix,
          cycler: row.cycler,
          kitLot: normalizedKitLot,
          mmLot: normalizedMmLot,
          waterLot: normalizedWaterLot,
          glycerolLot: normalizedGlycerolLot,
          antifoamBLot: normalizedAntifoamBLot,
          preparerInitials: normalizedPreparerInitials,
          uploaderInitials: normalizedUploaderInitials,
          overwriteExisting: overwriteRequested ? true : undefined,
        };

        uploads.push({
          rowId: row.id,
          metadata,
          ctsFile: row.ctsFile,
          rawFile: row.rawFile,
        });
      }

      const payload = buildSmartChipRunUpdatePayload(normalizedMmLot, row.id);
      const shouldTrackPendingSavedRunLog = row.chipId == null && row.persistedId == null;

      const chipLabel = resolvedChipName ? resolvedChipName : 'this chip';
      const successMessage =
        uploads.length > 0
          ? `Files saved for ${chipLabel}. Import queued below; you can leave this page.`
          : resolvedChipName
            ? `Run details saved for ${chipLabel}.`
            : 'SmartChip run details saved.';

      if (shouldTrackPendingSavedRunLog) {
        setPendingSavedChiplessRunLogIds(previous => {
          if (previous.has(row.id)) {
            return previous;
          }
          const next = new Set(previous);
          next.add(row.id);
          return next;
        });
      }

      patchRunLog(row.id, { fileError: null });
      try {
        await mutation.mutateAsync({
          workbookId: workbook.id,
          payload,
          successMessage,
          uploads,
        });
        if (uploads.length > 0) {
          patchRunLog(row.id, { ctsFile: null, rawFile: null, fileError: null });
        }
      } catch (saveError) {
        const message = saveError instanceof Error ? saveError.message : 'Unable to save run.';
        const affectedRows = getSmartChipNameFromSaveError(message)
          ? runLogs.filter(candidate =>
              isSmartChipNameSaveErrorForRow(message, resolveChipName(candidate, chips), candidate.chipId),
            )
          : [row];
        affectedRows.forEach(candidate => {
          patchRunLog(candidate.id, { fileError: message });
        });
        if (shouldTrackPendingSavedRunLog && !workbookPatchCompletedBeforeError(saveError)) {
          setPendingSavedChiplessRunLogIds(previous => {
            if (!previous.has(row.id)) {
              return previous;
            }
            const next = new Set(previous);
            next.delete(row.id);
            return next;
          });
        }
        // Error is surfaced via mutation.onError
      }
    },
    [
      buildSmartChipRunUpdatePayload,
      chips,
      duplicateChipNameConflictByRowId,
      mutation,
      patchRunLog,
      runDetailsReadOnly,
      runLogs,
      toast,
      viewerAccess.isResistomapAdmin,
      workbook,
    ],
  );

  const handleStatusChange = useCallback(
    (status: LabWorkbookStatus) => {
      if (runDetailsReadOnly || !workbook || workbook.status === status) {
        return;
      }
      mutation.mutate({
        workbookId: workbook.id,
        payload: { status },
        successMessage: `Status updated to ${status}.`,
      });
    },
    [mutation, runDetailsReadOnly, workbook],
  );

  const isMutationPending = mutation.isPending;

  if (!viewerAccess.canViewRun) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <AlertDescription>You do not have access to the SmartChip run step for this lab workbook.</AlertDescription>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Center minH="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : 'Failed to load workbook';
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{message}</AlertDescription>
      </Alert>
    );
  }

  if (!workbook) {
    return null;
  }

  const labSheetUrl = sampleSheet ? sampleSheet.labSheetUrl : null;
  const channelUrl = workbook.channelUrl;
  const workbookName = workbook.name ? workbook.name.trim() : '';
  const baseHeaderTitle = 'SmartChip Run';
  const headerTitle = workbookName ? `${workbookName} — ${baseHeaderTitle}` : baseHeaderTitle;
  const canStartRun = !runDetailsReadOnly && workbook.status === LabWorkbookStatus.SAMPLES_PREPPED;

  return (
    <VStack align="stretch" spacing={6}>
      <LabPageHeader
        title={headerTitle}
        subtitle="Review SmartChip layouts and record run details."
        labSheetUrl={labSheetUrl}
        channelUrl={channelUrl}
      />

      <Box borderWidth="1px" borderRadius="md" padding={4}>
        <VStack align="stretch" spacing={4}>
          <Text fontSize="sm" fontWeight="semibold" textTransform="uppercase" color="gray.600">
            Workbook context
          </Text>
          <Text color="gray.700">
            <Text as="span" fontWeight="semibold">
              Project lead:
            </Text>{' '}
            {hasProjectLead ? projectLeadDisplay : 'Not assigned'}
          </Text>
          <Text color="gray.600">Lab initials are recorded per chip below when entering run details.</Text>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={1}>
                Primers — where to find
              </Text>
              <Text color={workbook.primerStorageFindLocation ? 'gray.800' : 'gray.500'}>
                {workbook.primerStorageFindLocation ?? 'Not specified'}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={1}>
                Primers — where to leave
              </Text>
              <Text color={workbook.primerStorageLeaveLocation ? 'gray.800' : 'gray.500'}>
                {workbook.primerStorageLeaveLocation ?? 'Not specified'}
              </Text>
            </Box>
          </SimpleGrid>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={1}>
                Samples — where to find
              </Text>
              <Text color={workbook.sampleStorageFindLocation ? 'gray.800' : 'gray.500'}>
                {workbook.sampleStorageFindLocation ?? 'Not specified'}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={1}>
                Samples — where to leave
              </Text>
              <Text color={workbook.sampleStorageLeaveLocation ? 'gray.800' : 'gray.500'}>
                {workbook.sampleStorageLeaveLocation ?? 'Not specified'}
              </Text>
            </Box>
          </SimpleGrid>
          <Box>
            <Text fontWeight="semibold" color="gray.700" mb={1}>
              Lab partner note
            </Text>
            <Text color={hasLabPartnerNote ? 'gray.800' : 'gray.600'} whiteSpace="pre-wrap">
              {hasLabPartnerNote ? labPartnerNoteRaw ?? '' : 'No lab partner note added yet.'}
            </Text>
          </Box>
          {showStatusUpdatedBy ? (
            <Text color="gray.700">
              <Text as="span" fontWeight="semibold">
                Last status update by:
              </Text>{' '}
              {statusUpdatedByDisplay}
            </Text>
          ) : null}
        </VStack>
      </Box>
      {runDetailsReadOnly && workbook.status !== LabWorkbookStatus.LEGACY ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>SmartChip run details are read only after completing the run.</AlertDescription>
        </Alert>
      ) : null}
      <Button
        colorScheme="orange"
        variant="outline"
        onClick={() => {
          handleStatusChange(LabWorkbookStatus.RUNNING);
        }}
        isDisabled={isMutationPending || !canStartRun}
        isLoading={isMutationPending}
        alignSelf="flex-start"
      >
        {workbook.status === LabWorkbookStatus.RUNNING
          ? 'SmartChip Preparation Started'
          : 'Proceed to SmartChip preparation'}
      </Button>

      <Box borderWidth="1px" borderRadius="md" padding={4}>
        <Heading size="md" fontSize="lg" mb={3}>
          Runs
        </Heading>
        {chipTabs.length === 0 ? (
          <Text color="gray.600">Sample layouts will display once this workbook has derived placement data.</Text>
        ) : (
          <Tabs
            index={activeTabIndex}
            onChange={tabIndex => {
              setSelectedLayoutIndex(tabIndex);
            }}
          >
            <TabList overflowX="auto">
              {chipTabs.map(tab => {
                const runLog = resolveRunLogForTab(tab);
                const layoutIsComplete = tab.layout?.isComplete ?? false;
                const tabIsSelectable = canUseSmartChipRunTabDetails({
                  canViewRun: viewerAccess.canViewRun,
                  isPlaceholder: tab.isPlaceholder,
                  isEnabled: tab.isEnabled,
                  layoutIsComplete,
                });
                const tabLabelCandidate = runLog?.chipName?.trim() ?? '';
                const tabLabel =
                  tabLabelCandidate.length > 0 ? tabLabelCandidate : tab.chipDisplayName ?? tab.defaultLabel;
                return (
                  <Tab
                    key={tab.key}
                    isDisabled={!tabIsSelectable}
                    flexShrink={0}
                    whiteSpace="nowrap"
                    title={tabLabel}
                    _selected={{ color: 'blue.400', boxShadow: 'inset 0 -2px 0 currentColor' }}
                    ref={node => {
                      if (node) {
                        chipTabButtonByKeyRef.current.set(tab.key, node);
                      } else {
                        chipTabButtonByKeyRef.current.delete(tab.key);
                      }
                    }}
                  >
                    {tabLabel}
                  </Tab>
                );
              })}
            </TabList>
            <TabPanels mt={4}>
              {chipTabs.map(tab => {
                const runLog = resolveRunLogForTab(tab);
                const layoutIsComplete = tab.layout?.isComplete ?? false;
                const canUseRunDetails = canUseSmartChipRunTabDetails({
                  canViewRun: viewerAccess.canViewRun,
                  isPlaceholder: tab.isPlaceholder,
                  isEnabled: tab.isEnabled,
                  layoutIsComplete,
                });
                const tabAllowsFileUpload = canUploadSmartChipRunFilesForTab({
                  isPlaceholder: tab.isPlaceholder,
                  isEnabled: tab.isEnabled,
                });
                const tabIsLocked = !canUseRunDetails;
                const rowRepresentsPersistedChip = Boolean(runLog?.chipId) && !tab.isPlaceholder;
                const runStepLocked = runDetailsReadOnly || isMutationPending || !canUseRunDetails;
                const runFileUploadLocked = runDetailsReadOnly || isMutationPending || !tabAllowsFileUpload;
                const canEditStartRun = !runStepLocked && !rowRepresentsPersistedChip;
                const canEditRecordRun = !runStepLocked;
                const canEditChipName =
                  !runDetailsReadOnly &&
                  !isMutationPending &&
                  (canEditStartRun || (tab.isPlaceholder && !tab.isEnabled && layoutIsComplete));
                const showResultUploadLockedNotice = !runDetailsReadOnly && canUseRunDetails && !tabAllowsFileUpload;
                const requiresPreparerInitials = runLog ? rowRequiresPreparerInitials(runLog) : false;
                const requiresUploaderInitials = runLog ? rowRequiresUploaderInitials(runLog) : false;
                const preparerInitialsValid = runLog ? rowPreparerInitialsAreValid(runLog) : true;
                const uploaderInitialsValid = runLog ? rowUploaderInitialsAreValid(runLog) : true;
                const startInitialsInvalid = Boolean(runLog && requiresPreparerInitials && !preparerInitialsValid);
                const recordInitialsInvalid = Boolean(runLog && requiresUploaderInitials && !uploaderInitialsValid);
                const initialsInvalid = startInitialsInvalid || recordInitialsInvalid;
                const trimmedMastermixLot = runLog ? runLog.mmLot.trim() : '';
                const trimmedWaterLot = runLog ? runLog.waterLot.trim() : '';
                const trimmedGlycerolLot = runLog ? runLog.glycerolLot.trim() : '';
                const trimmedAntifoamBLot = runLog ? runLog.antifoamBLot.trim() : '';
                const trimmedKitLot = runLog && runLog.kitLot ? runLog.kitLot.trim() : '';
                const resolvedChipName = runLog ? resolveChipName(runLog, chips) : null;
                const startComplete =
                  !!runLog &&
                  (rowRepresentsPersistedChip ||
                    (trimmedMastermixLot.length > 0 &&
                      trimmedWaterLot.length > 0 &&
                      trimmedGlycerolLot.length > 0 &&
                      trimmedAntifoamBLot.length > 0 &&
                      trimmedKitLot.length > 0 &&
                      resolvedChipName != null &&
                      runLog.runDate.trim().length > 0 &&
                      (!requiresPreparerInitials || preparerInitialsValid) &&
                      runLog.dispenser != null &&
                      runLog.cycler != null));
                const recordComplete =
                  !!runLog &&
                  ((runLog.chipId != null && chips.some(chip => chip.id === runLog.chipId)) ||
                    (runLog.chipIndex != null && runLog.chipIndex >= 0 && runLog.chipIndex < chips.length));
                const pipetSamplesComplete = startComplete || Boolean(tab.layout);
                const pipetPrimersComplete = startComplete || Boolean(smartChip?.assayGrid);
                const pipetSamplesIcon = pipetSamplesComplete ? faCheck : faSquareRegular;
                const pipetPrimersIcon = pipetPrimersComplete ? faCheck : faSquareRegular;
                const pipetSamplesIconColor = pipetSamplesComplete ? 'green.500' : 'gray.400';
                const pipetPrimersIconColor = pipetPrimersComplete ? 'green.500' : 'gray.400';
                const startIcon = startComplete ? faCheck : faSquareRegular;
                const recordIcon = recordComplete ? faCheck : faSquareRegular;
                const startIconColor = startComplete ? 'green.500' : 'gray.400';
                const recordIconColor = recordComplete ? 'green.500' : 'gray.400';
                const chipNameInputErrorMessage = (() => {
                  if (!runLog || !canEditChipName) {
                    return null;
                  }
                  if (duplicateChipNameConflictByRowId.has(runLog.id)) {
                    return DUPLICATE_CHIP_NAME_MESSAGE;
                  }
                  const fileError = runLog.fileError;
                  if (fileError && isSmartChipNameSaveErrorForRow(fileError, resolvedChipName, runLog.chipId)) {
                    return fileError;
                  }
                  return null;
                })();
                const chipIndexForDownload = runLog?.chipIndex ?? tab.chipIndex ?? tab.layout?.chipIndex ?? null;
                const fallbackDownloadLabel = tab.chipDisplayName ?? tab.defaultLabel;
                const downloadFileName = resolvedChipName
                  ? buildChipFilename(resolvedChipName, fallbackDownloadLabel)
                  : buildChipFilename('', fallbackDownloadLabel);
                const isDownloading = runLog ? downloadingRows.has(runLog.id) : false;
                const canDownloadMyDesign = Boolean(
                  tab.layout && smartChip?.assayGrid && layoutIsComplete && resolvedChipName,
                );
                const selectedPipettingSystem = runLog ? resolvePipettingSystem(runLog) : 'MSND';
                if (tabIsLocked) {
                  const expectedSamples = tab.layout?.samplesPerChip ?? null;
                  const sampleCount = tab.layout?.sampleCount ?? null;
                  const samplesNeeded =
                    expectedSamples != null && sampleCount != null ? Math.max(expectedSamples - sampleCount, 0) : null;
                  return (
                    <TabPanel key={tab.key} px={0}>
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription>
                          {samplesNeeded != null && expectedSamples != null
                            ? `Add ${samplesNeeded} more sample${samplesNeeded === 1 ? '' : 's'} to unlock ${
                                tab.defaultLabel
                              } (${sampleCount}/${expectedSamples} samples submitted).`
                            : `This chip unlocks once enough samples are submitted for ${tab.defaultLabel}.`}
                        </AlertDescription>
                      </Alert>
                    </TabPanel>
                  );
                }

                return (
                  <TabPanel key={tab.key} px={0}>
                    <VStack align="stretch" spacing={6}>
                      {runLog ? (
                        <VStack align="stretch" spacing={6}>
                          {showResultUploadLockedNotice ? (
                            <Alert status="info" borderRadius="md">
                              <AlertIcon />
                              <AlertDescription>
                                Run details can be filled now. Results can be uploaded after earlier chip results are
                                uploaded.
                              </AlertDescription>
                            </Alert>
                          ) : null}
                          <Accordion allowToggle defaultIndex={[0]} reduceMotion>
                            <AccordionItem border="none">
                              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                <Flex flex="1" justify="space-between" align="center">
                                  <HStack spacing={3} align="center">
                                    <Box color={pipetPrimersIconColor} mt="2px">
                                      <FontAwesomeIcon icon={pipetPrimersIcon} />
                                    </Box>
                                    <Heading size="md" fontSize="lg">
                                      1. Pipette primers and mastermix
                                    </Heading>
                                  </HStack>
                                  <AccordionIcon />
                                </Flex>
                              </AccordionButton>
                              <AccordionPanel px={0} pt={3}>
                                <VStack align="stretch" spacing={4}>
                                  <SmartChipPipettingVolumes
                                    configurationId={configurationId}
                                    samplesPerChip={tab.layout?.samplesPerChip ?? null}
                                    step="primer"
                                    system={selectedPipettingSystem}
                                    onSystemChange={
                                      resolvePipettingSystemFromDispenserPrefix(runLog.dispenserPrefix) == null
                                        ? nextSystem => {
                                            handlePipettingSystemChange(runLog.id, nextSystem);
                                          }
                                        : undefined
                                    }
                                  />
                                  {smartChip?.assayGrid ? (
                                    <SmartChipGridView
                                      grid={smartChip.assayGrid}
                                      banding={{ orientation: 'row', step: 1, colors: ['#EECDCD', '#789DE5'] }}
                                    />
                                  ) : (
                                    <Alert status="info" borderRadius="md">
                                      <AlertIcon />
                                      <AlertDescription>
                                        Assay layout will appear here once the workbook derivations are available.
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>
                          <Accordion allowToggle defaultIndex={[0]} reduceMotion>
                            <AccordionItem border="none">
                              <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                                <Flex flex="1" justify="space-between" align="center">
                                  <HStack spacing={3} align="center">
                                    <Box color={pipetSamplesIconColor} mt="2px">
                                      <FontAwesomeIcon icon={pipetSamplesIcon} />
                                    </Box>
                                    <Heading size="md" fontSize="lg">
                                      2. Pipette samples
                                    </Heading>
                                  </HStack>
                                  <AccordionIcon />
                                </Flex>
                              </AccordionButton>
                              <AccordionPanel px={0} pt={3}>
                                <VStack align="stretch" spacing={4}>
                                  <SmartChipPipettingVolumes
                                    configurationId={configurationId}
                                    samplesPerChip={tab.layout?.samplesPerChip ?? null}
                                    step="sample"
                                    system={selectedPipettingSystem}
                                    showDispensingSystemSelector={false}
                                  />
                                  {tab.layout ? (
                                    <VStack align="stretch" spacing={6}>
                                      <SmartChipGridView
                                        grid={{ ...tab.layout.primary, caption: null }}
                                        banding={{ orientation: 'row', step: 1, colors: ['#EECDCD', '#789DE5'] }}
                                      />
                                      {tab.layout.secondary ? (
                                        <>
                                          <Divider />
                                          <SmartChipGridView
                                            title="Sample layout — plate 2"
                                            grid={{ ...tab.layout.secondary, caption: null }}
                                            banding={{ orientation: 'row', step: 1, colors: ['#EECDCD', '#789DE5'] }}
                                          />
                                        </>
                                      ) : null}
                                      <Flex justify="flex-end">
                                        <Button
                                          leftIcon={<FontAwesomeIcon icon={faDownload} />}
                                          colorScheme="blue"
                                          variant="outline"
                                          onClick={() => {
                                            void handleDownloadMyDesign(runLog, {
                                              chipIndex: chipIndexForDownload,
                                              fallbackLabel: fallbackDownloadLabel,
                                            });
                                          }}
                                          isDisabled={!canDownloadMyDesign}
                                          isLoading={isDownloading}
                                        >
                                          Download {downloadFileName}
                                        </Button>
                                      </Flex>
                                    </VStack>
                                  ) : (
                                    <Alert status="info" borderRadius="md">
                                      <AlertIcon />
                                      <AlertDescription>
                                        Sample layout data is not available for this chip yet.
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>

                          <VStack align="stretch" spacing={4}>
                            <HStack spacing={3} align="center">
                              <Box color={startIconColor} mt="2px">
                                <FontAwesomeIcon icon={startIcon} />
                              </Box>
                              <Heading size="md" fontSize="lg">
                                3. Start dispensing run
                              </Heading>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
                              <FormControl
                                isRequired
                                isInvalid={!runDetailsReadOnly && Boolean(chipNameInputErrorMessage)}
                              >
                                <FormLabel>Chip name</FormLabel>
                                <Input
                                  value={runLog.chipName ?? ''}
                                  placeholder="Enter chip name"
                                  onChange={event => {
                                    if (!canEditChipName) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, { chipName: event.target.value, fileError: null });
                                  }}
                                  isReadOnly={!canEditChipName}
                                  isDisabled={!canEditChipName}
                                />
                                <FormErrorMessage>{chipNameInputErrorMessage ?? 'Enter a chip name.'}</FormErrorMessage>
                              </FormControl>
                              <FormControl isRequired={canEditStartRun}>
                                <FormLabel>Run date</FormLabel>
                                <Input
                                  type="date"
                                  value={runLog.runDate}
                                  onChange={event => {
                                    if (!canEditStartRun) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, { runDate: event.target.value });
                                  }}
                                  isDisabled={!canEditStartRun}
                                />
                              </FormControl>
                              <FormControl isRequired={canEditStartRun}>
                                <FormLabel>Dispenser</FormLabel>
                                <Select
                                  placeholder="Select dispenser"
                                  value={(() => {
                                    if (runLog.dispenser == null) return '';
                                    const prefix = runLog.dispenserPrefix ?? 'MSND';
                                    return `${prefix}:${runLog.dispenser}`;
                                  })()}
                                  onChange={event => {
                                    if (!canEditStartRun) {
                                      return;
                                    }
                                    const raw = event.target.value;
                                    if (!raw) {
                                      patchRunLog(runLog.id, { dispenser: null, dispenserPrefix: null });
                                      return;
                                    }
                                    const option = DISPENSER_OPTIONS.find(o => o.valueKey === raw);
                                    if (!option) {
                                      patchRunLog(runLog.id, { dispenser: null, dispenserPrefix: null });
                                      return;
                                    }
                                    patchRunLog(runLog.id, { dispenser: option.value, dispenserPrefix: option.prefix });
                                  }}
                                  isDisabled={!canEditStartRun}
                                >
                                  {DISPENSER_OPTIONS.map(option => (
                                    <option key={option.valueKey} value={option.valueKey}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControl isRequired={canEditStartRun}>
                                <FormLabel>Cycler</FormLabel>
                                <Select
                                  placeholder="Select cycler"
                                  value={runLog.cycler != null ? String(runLog.cycler) : ''}
                                  onChange={event => {
                                    if (!canEditStartRun) {
                                      return;
                                    }
                                    const nextValue = event.target.value ? Number(event.target.value) : null;
                                    patchRunLog(runLog.id, { cycler: nextValue });
                                  }}
                                  isDisabled={!canEditStartRun}
                                >
                                  {CYCLER_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                              {SHARED_LOT_FIELDS.map(field => (
                                <FormControl key={field.key} isRequired={canEditStartRun}>
                                  <FormLabel>{field.label}</FormLabel>
                                  <Input
                                    value={runLog[field.key]}
                                    placeholder={field.placeholder}
                                    onChange={event => {
                                      if (!canEditStartRun) {
                                        return;
                                      }
                                      patchRunLog(runLog.id, {
                                        [field.key]: event.target.value,
                                      } as Pick<RunLogFormRow, SharedLotFieldKey>);
                                    }}
                                    isReadOnly={!canEditStartRun}
                                    isDisabled={!canEditStartRun}
                                    variant={canEditStartRun ? 'outline' : 'filled'}
                                  />
                                </FormControl>
                              ))}
                              {PER_CHIP_LOT_FIELDS.map(field => (
                                <FormControl key={field.key} isRequired={canEditStartRun}>
                                  <FormLabel>{field.label}</FormLabel>
                                  <Input
                                    value={runLog[field.key]}
                                    placeholder={field.placeholder}
                                    onChange={event => {
                                      if (!canEditStartRun) {
                                        return;
                                      }
                                      patchRunLog(runLog.id, {
                                        [field.key]: event.target.value,
                                      } as Pick<RunLogFormRow, KitLotFieldKey>);
                                    }}
                                    isReadOnly={!canEditStartRun}
                                    isDisabled={!canEditStartRun}
                                  />
                                </FormControl>
                              ))}
                              <FormControl
                                isRequired={canEditStartRun && requiresPreparerInitials}
                                isInvalid={
                                  !runDetailsReadOnly &&
                                  canEditStartRun &&
                                  requiresPreparerInitials &&
                                  !isValidInitials(runLog.preparerInitials)
                                }
                              >
                                <FormLabel>Prepared by initials</FormLabel>
                                <Input
                                  value={runLog.preparerInitials}
                                  placeholder="ABC"
                                  maxLength={3}
                                  autoComplete="off"
                                  onChange={event => {
                                    if (!canEditStartRun) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, {
                                      preparerInitials: normalizeInitialsInput(event.target.value),
                                    });
                                  }}
                                  onBlur={event => {
                                    if (!canEditStartRun) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, {
                                      preparerInitials: normalizeInitialsInput(event.target.value),
                                    });
                                  }}
                                  isDisabled={!canEditStartRun}
                                />
                                <FormErrorMessage>Enter three letters (A-Z).</FormErrorMessage>
                              </FormControl>
                            </SimpleGrid>
                            <Flex justify="flex-end">
                              <Button
                                colorScheme="blue"
                                variant="outline"
                                onClick={() => {
                                  if (!canEditStartRun) {
                                    return;
                                  }
                                  void handleSaveRunDetails(runLog);
                                }}
                                isDisabled={!canEditStartRun || startInitialsInvalid || isMutationPending}
                                isLoading={isMutationPending}
                              >
                                Save start details
                              </Button>
                            </Flex>
                          </VStack>
                          <Divider />
                          <VStack align="stretch" spacing={4}>
                            <HStack spacing={3} align="center">
                              <Box color={recordIconColor} mt="2px">
                                <FontAwesomeIcon icon={recordIcon} />
                              </Box>
                              <Heading size="md" fontSize="lg">
                                4. Record cycling run
                              </Heading>
                            </HStack>
                            <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
                              <FormControl
                                isRequired={canEditRecordRun && requiresUploaderInitials}
                                isInvalid={
                                  canEditRecordRun &&
                                  requiresUploaderInitials &&
                                  !isValidInitials(runLog.uploaderInitials)
                                }
                              >
                                <FormLabel>Uploaded by initials</FormLabel>
                                <Input
                                  value={runLog.uploaderInitials}
                                  placeholder="XYZ"
                                  maxLength={3}
                                  autoComplete="off"
                                  onChange={event => {
                                    if (!canEditRecordRun) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, {
                                      uploaderInitials: normalizeInitialsInput(event.target.value),
                                    });
                                  }}
                                  onBlur={event => {
                                    if (!canEditRecordRun) {
                                      return;
                                    }
                                    patchRunLog(runLog.id, {
                                      uploaderInitials: normalizeInitialsInput(event.target.value),
                                    });
                                  }}
                                  isReadOnly={!canEditRecordRun}
                                  isDisabled={!canEditRecordRun}
                                />
                                <FormErrorMessage>Enter three letters (A-Z).</FormErrorMessage>
                              </FormControl>
                            </SimpleGrid>
                            <FormControl>
                              <FormLabel>Comment</FormLabel>
                              <Textarea
                                value={runLog.comment}
                                onChange={event => {
                                  if (!canEditRecordRun) {
                                    return;
                                  }
                                  patchRunLog(runLog.id, { comment: event.target.value });
                                }}
                                placeholder="Add optional context or notes for this chip run"
                                minH={10}
                                isReadOnly={!canEditRecordRun}
                                isDisabled={!canEditRecordRun}
                              />
                            </FormControl>
                            <SmartChipRunFilesDropzone
                              ctsFile={runLog.ctsFile}
                              rawFile={runLog.rawFile}
                              error={runLog.fileError}
                              onChange={update => {
                                handleRunLogFilesChange(runLog.id, update);
                              }}
                              existingFiles={runLog.existingFiles}
                              disabled={runFileUploadLocked}
                              ctsTableHref={
                                runLog.chipId != null
                                  ? `/lab/${workbook.id}/smartchip-run/${runLog.chipId}/cts-table`
                                  : null
                              }
                              allowOverwrite={viewerAccess.isResistomapAdmin}
                              allowCtsUpload={viewerAccess.isResistomapAdmin}
                            />
                            <Flex justify="flex-end">
                              <Button
                                colorScheme="blue"
                                onClick={() => {
                                  if (!canEditRecordRun) {
                                    return;
                                  }
                                  void handleSaveRunDetails(runLog);
                                }}
                                isDisabled={!canEditRecordRun || initialsInvalid || isMutationPending}
                                isLoading={isMutationPending}
                                loadingText="Saving and processing…"
                              >
                                Save run details
                              </Button>
                            </Flex>
                          </VStack>
                        </VStack>
                      ) : (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription>No run entry is available for this chip yet.</AlertDescription>
                        </Alert>
                      )}
                    </VStack>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        )}
      </Box>
      <ChipImportJobs workbookId={workbook.id} />
      {viewerAccess.isResistomapAdmin ? (
        <Box borderWidth="1px" borderRadius="md" padding={4}>
          <Heading size="md" fontSize="lg" mb={3}>
            Links
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={2}>
                Projects
              </Text>
              {linkedProjects.length > 0 ? (
                <VStack align="stretch" spacing={1}>
                  {linkedProjects.map(projectLink => (
                    <Button
                      key={projectLink.id}
                      as={RouterLink}
                      to={`/research/${projectLink.id}`}
                      variant="link"
                      colorScheme="blue"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      whiteSpace="normal"
                    >
                      {projectLink.name}
                    </Button>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500">No linked projects yet.</Text>
              )}
            </Box>
            <Box>
              <Text fontWeight="semibold" color="gray.700" mb={2}>
                Chips
              </Text>
              {chips.length > 0 ? (
                <VStack align="stretch" spacing={1}>
                  {chips.map(chip => (
                    <Button
                      key={chip.id}
                      as={RouterLink}
                      to={`/chip/${chip.id}`}
                      variant="link"
                      colorScheme="blue"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      whiteSpace="normal"
                    >
                      {chip.name}
                    </Button>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500">No chips recorded yet.</Text>
              )}
            </Box>
          </SimpleGrid>
        </Box>
      ) : null}
    </VStack>
  );
}

type SmartChipRunFilesDropzoneProps = {
  ctsFile: File | null;
  rawFile: File | null;
  error: string | null;
  onChange: (update: RunLogFilesUpdate) => void;
  disabled?: boolean;
  existingFiles: RunLogExistingFiles | null;
  ctsTableHref: string | null;
  allowOverwrite?: boolean;
  allowCtsUpload?: boolean;
};

function SmartChipRunFilesDropzone({
  ctsFile,
  rawFile,
  error,
  onChange,
  disabled = false,
  existingFiles,
  ctsTableHref,
  allowOverwrite = false,
  allowCtsUpload = false,
}: SmartChipRunFilesDropzoneProps) {
  const toast = useToast();
  const hasExistingCts = Boolean(existingFiles?.cts);
  const hasExistingRaw = Boolean(existingFiles?.raw);
  const canOverwrite = allowOverwrite && hasExistingCts && hasExistingRaw;
  const overwriteReady = canOverwrite && Boolean(rawFile);
  const overwriteQueued = canOverwrite && !overwriteReady && Boolean(ctsFile || rawFile);
  const highlightOverwrite = !disabled && canOverwrite && Boolean(ctsFile || rawFile);
  const acceptedFilesDescription = allowCtsUpload
    ? 'one Raw (.Raw) file and, optionally, one CTS (.csv/.txt) file'
    : 'one Raw (.Raw) file';

  const handleFilesSelected = useCallback(
    (files: File[], clearError: boolean) => {
      if (disabled) {
        return;
      }
      if (!files.length) {
        return;
      }
      const classification = files.reduce(
        (result, file) => {
          const name = file.name.toLowerCase();
          if (allowCtsUpload && (name.endsWith('.csv') || name.endsWith('.txt'))) {
            result.cts.push(file);
          } else if (name.endsWith('.raw')) {
            result.raw.push(file);
          } else {
            result.unsupported.push(file);
          }
          return result;
        },
        { cts: [] as File[], raw: [] as File[], unsupported: [] as File[] },
      );

      if (classification.unsupported.length) {
        const unsupportedNames = classification.unsupported.map(file => file.name).join(', ');
        onChange({
          fileError: `Unsupported file type${classification.unsupported.length > 1 ? 's' : ''}: ${unsupportedNames}. Please select ${acceptedFilesDescription}.`,
        });
        return;
      }

      if (classification.cts.length > 1 || classification.raw.length > 1) {
        onChange({ fileError: `Please select ${acceptedFilesDescription}.` });
        return;
      }

      if (classification.cts.length > 0 && hasExistingCts && !canOverwrite) {
        onChange({ fileError: 'CTS file already uploaded for this chip.' });
        return;
      }

      if (classification.raw.length > 0 && hasExistingRaw && !canOverwrite) {
        onChange({ fileError: 'Raw file already uploaded for this chip.' });
        return;
      }

      const update: RunLogFilesUpdate = {};
      if (classification.cts.length === 1) {
        update.ctsFile = classification.cts[0];
      }
      if (classification.raw.length === 1) {
        update.rawFile = classification.raw[0];
      }
      if (clearError) {
        update.fileError = null;
      }
      if (Object.keys(update).length > 0) {
        onChange(update);
      }
    },
    [acceptedFilesDescription, allowCtsUpload, canOverwrite, disabled, hasExistingCts, hasExistingRaw, onChange],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (disabled) {
        return;
      }
      const hasRejections = fileRejections.length > 0;
      if (hasRejections) {
        const hasTooManyFiles = fileRejections.some(rejection =>
          rejection.errors.some(errorDetail => errorDetail.code === 'too-many-files'),
        );
        const rejectedNames = fileRejections.map(rejection => rejection.file.name).join(', ');
        onChange({
          fileError: hasTooManyFiles
            ? `Please select ${acceptedFilesDescription}.`
            : `Unsupported file type${fileRejections.length > 1 ? 's' : ''}: ${rejectedNames}. Please select ${acceptedFilesDescription}.`,
        });
      }
      if (acceptedFiles.length) {
        handleFilesSelected(acceptedFiles, !hasRejections);
      }
    },
    [acceptedFilesDescription, disabled, handleFilesSelected, onChange],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      ...(allowCtsUpload ? { 'text/csv': ['.csv'], 'text/plain': ['.txt'] } : {}),
      'application/octet-stream': ['.raw', '.Raw'],
    },
    multiple: allowCtsUpload,
    maxFiles: allowCtsUpload ? 2 : 1,
    noClick: true,
    onDrop,
    disabled,
  });

  const rootProps = getRootProps({
    onClick: event => {
      event.preventDefault();
      open();
    },
  });
  const inputProps = getInputProps();
  const hasCts = Boolean(ctsFile || hasExistingCts);
  const hasRaw = Boolean(rawFile || hasExistingRaw);
  const filesReady = canOverwrite ? overwriteReady : hasRaw;
  const fileEntries: Array<{
    key: 'cts' | 'raw';
    label: string;
    file: File | null;
    existing: RunLogRemoteFile | null;
    onClear: () => void;
  }> = [
    {
      key: 'raw',
      label: 'Raw instrument export (.Raw)',
      file: rawFile,
      existing: existingFiles?.raw ?? null,
      onClear: () => {
        if (disabled) {
          return;
        }
        onChange({ rawFile: null, fileError: null });
      },
    },
    {
      key: 'cts',
      label: hasExistingCts ? 'Results (.txt)' : 'CTS verification (.csv or .txt, optional)',
      file: ctsFile,
      existing: existingFiles?.cts ?? null,
      onClear: () => {
        if (disabled) {
          return;
        }
        onChange({ ctsFile: null, fileError: null });
      },
    },
  ];

  const apiBaseUrl = useMemo(() => API_URL.replace(/\/api$/, ''), []);

  const handleDownload = useCallback(
    async (file: RunLogRemoteFile) => {
      const targetUrl = file.url.startsWith('http') ? file.url : `${apiBaseUrl}${file.url}`;
      const isApiUrl = !file.url.startsWith('http') || targetUrl.startsWith(apiBaseUrl);

      if (!isApiUrl) {
        const link = document.createElement('a');
        link.href = targetUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      try {
        const isRawDownload = file.filename.toLowerCase().endsWith('.raw');
        const headers = getAuthHeaders();

        if (isRawDownload) {
          // Large downloads can be expensive to buffer into a Blob (and have historically been flaky under CI).
          // Ensure the JWT cookie is present (set by /api/user when authenticating via Bearer token),
          // then let the browser handle the streaming download directly.
          try {
            await fetch(`${apiBaseUrl}/api/user`, {
              method: 'GET',
              headers,
              credentials: 'include',
            });
          } catch {
            // Ignore auth cookie preflight errors and fall back to the buffered download below.
          }

          const rawDownloadLink = document.createElement('a');
          rawDownloadLink.href = targetUrl;
          rawDownloadLink.download = file.filename;
          document.body.appendChild(rawDownloadLink);
          rawDownloadLink.click();
          document.body.removeChild(rawDownloadLink);
          return;
        }

        const response = await fetch(targetUrl, {
          method: 'GET',
          headers,
          credentials: 'include',
        });

        if (!response.ok) {
          const cloned = response.clone();
          let errorMessage = `Download failed with status ${response.status}.`;
          try {
            const errorText = await cloned.text();
            if (errorText.trim().length > 0) {
              try {
                const parsed = JSON.parse(errorText) as { message?: string };
                if (parsed.message && parsed.message.trim().length > 0) {
                  errorMessage = parsed.message;
                } else {
                  errorMessage = errorText;
                }
              } catch {
                errorMessage = errorText;
              }
            }
          } catch {
            // noop
          }
          throw new Error(errorMessage);
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = file.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (downloadError) {
        const message =
          downloadError instanceof Error ? downloadError.message : 'Unable to download SmartChip run file.';
        toast({
          status: 'error',
          title: 'Download failed',
          description: message,
          duration: 6000,
          isClosable: true,
        });
      }
    },
    [apiBaseUrl, toast],
  );

  return (
    <Box
      {...rootProps}
      borderWidth="2px"
      borderRadius="lg"
      borderStyle="dashed"
      borderColor={isDragActive ? 'blue.500' : highlightOverwrite ? 'orange.400' : 'gray.300'}
      backgroundColor={isDragActive ? 'blue.50' : highlightOverwrite ? 'orange.50' : 'gray.50'}
      padding={6}
      transition="border-color 0.2s ease, background-color 0.2s ease"
      cursor={disabled ? 'not-allowed' : 'pointer'}
    >
      <input {...inputProps} />
      <Stack spacing={4}>
        <Stack spacing={2} align="center" textAlign="center">
          <Text fontWeight="semibold">
            {disabled ? 'SmartChip run files' : canOverwrite ? 'Replace SmartChip results' : 'Drag & drop the Raw file'}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {disabled
              ? 'Run uploads are locked for this chip. Download existing files below.'
              : canOverwrite
                ? 'Select a new Raw file to recalculate and replace existing results when you save.'
                : 'Results are calculated automatically from Raw when you save.'}
          </Text>
          {allowCtsUpload && !disabled ? (
            <Text fontSize="sm" color="gray.600">
              Admin verification: optionally select a CTS export with its Raw in the same upload. Results must match
              exactly.
            </Text>
          ) : null}
          <Button
            variant="base"
            colorScheme="blue"
            size="sm"
            onClick={event => {
              event.stopPropagation();
              if (!disabled) {
                open();
              }
            }}
            isDisabled={disabled}
          >
            {allowCtsUpload ? 'Choose files' : 'Choose Raw file'}
          </Button>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          {fileEntries
            .filter(entry => entry.key === 'raw' || allowCtsUpload || hasExistingCts)
            .map(entry => {
              const existingFile = entry.existing;
              const hasFile = Boolean(entry.file || existingFile);
              return (
                <Flex key={entry.key} align="center" justify="space-between" gap={4} flexWrap="wrap">
                  <HStack spacing={2}>
                    <Box color={hasFile ? 'green.500' : 'gray.400'}>
                      <FontAwesomeIcon icon={hasFile ? faCheckCircle : faTimesCircle} size="sm" />
                    </Box>
                    <Text fontWeight="medium">{entry.label}</Text>
                  </HStack>
                  <HStack spacing={3} maxW="100%">
                    {existingFile ? (
                      entry.key === 'cts' && ctsTableHref ? (
                        <IconButton
                          as="a"
                          aria-label="Open CTS table"
                          href={ctsTableHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="link"
                          mb={1}
                          colorScheme="blue"
                          icon={<FontAwesomeIcon icon={faExternalLinkAlt} />}
                          onClick={event => {
                            event.stopPropagation();
                          }}
                        />
                      ) : null
                    ) : null}
                    <Stack spacing={0} maxW="220px">
                      <Text fontSize="sm" color={hasFile ? 'gray.600' : 'gray.500'} isTruncated>
                        {entry.file ? entry.file.name : existingFile?.filename ?? ''}
                      </Text>
                      {entry.file && existingFile ? (
                        <Text fontSize="xs" color="orange.600" isTruncated>
                          Replacing {existingFile.filename}
                        </Text>
                      ) : null}
                    </Stack>
                    {existingFile ? (
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        onClick={event => {
                          event.stopPropagation();
                          void handleDownload(existingFile);
                        }}
                      >
                        Download
                      </Button>
                    ) : null}
                    {entry.file && !disabled ? (
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        onClick={event => {
                          event.stopPropagation();
                          entry.onClear();
                        }}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </HStack>
                </Flex>
              );
            })}
        </Stack>
        {disabled ? null : canOverwrite ? (
          overwriteReady ? (
            <HStack color="orange.600" spacing={2}>
              <Box color="orange.500">
                <FontAwesomeIcon icon={faCheckCircle} size="sm" />
              </Box>
              <Text fontSize="sm">Overwrite ready. Saving will replace existing results.</Text>
            </HStack>
          ) : overwriteQueued ? (
            <Text fontSize="sm" color="orange.600">
              Select Raw to overwrite existing results.
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Results and Raw are stored. Select a new Raw file to replace them.
            </Text>
          )
        ) : filesReady ? (
          <HStack color="green.600" spacing={2}>
            <Box color="green.500">
              <FontAwesomeIcon icon={faCheckCircle} size="sm" />
            </Box>
            <Text fontSize="sm">
              {hasExistingCts
                ? 'Results and Raw are stored.'
                : ctsFile
                  ? 'Raw and CTS ready. Results will be verified when you save.'
                  : 'Raw ready. Results will be calculated when you save.'}
            </Text>
          </HStack>
        ) : (
          <Text fontSize="sm" color="gray.500">
            {hasCts ? 'CTS selected. Select its Raw file before saving.' : 'Select Raw to calculate results.'}
          </Text>
        )}
        {error ? (
          <Text color="red.500" fontSize="sm">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}
